import cloneDeep from 'lodash.clonedeep';

import { PubSubService } from '../_shared/pubSubServiceInterface';
import sortBy from '../../utils/sortBy';
import ProtocolEngine from './ProtocolEngine';
import { StudyMetadata } from '../../types/StudyMetadata';
import DisplaySet from '../DisplaySetService/DisplaySet';
import { CommandsManager } from '../../classes';
import * as HangingProtocol from '../../types/HangingProtocol';
import { isDisplaySetFromUrl, sopInstanceLocation } from './custom-attribute/isDisplaySetFromUrl';
import numberOfDisplaySetsWithImages from './custom-attribute/numberOfDisplaySetsWithImages';
import seriesDescriptionsFromDisplaySets from './custom-attribute/seriesDescriptionsFromDisplaySets';
import uuidv4 from '../../utils/uuidv4';

type Protocol = HangingProtocol.Protocol | HangingProtocol.ProtocolGenerator;

const DEFAULT_VIEWPORT_OPTIONS: HangingProtocol.ViewportOptions = {
  toolGroupId: 'default',
  viewportType: 'stack',
};

export default class HangingProtocolService extends PubSubService {
  static EVENTS = {
    // The PROTOCOL_CHANGED event is fired when the protocol changes
    // and should be immediately applied
    PROTOCOL_CHANGED: 'event::hanging_protocol_changed',
    // The PROTOCOL_RESTORED event is fired instead of a changed event to indicate
    // that an earlier state has been restored as part of a state update, but
    // is not being directly re-applied, but just restored.
    PROTOCOL_RESTORED: 'event::hanging_protocol_restore',
    // The layout has been decided for the hanging protocol - deprecated
    NEW_LAYOUT: 'event::hanging_protocol_new_layout',
    // Fired when the stages within the current protocol are known to have
    // the status set - that is, they are activated (or deactivated).
    STAGE_ACTIVATION: 'event::hanging_protocol_stage_activation',
    CUSTOM_IMAGE_LOAD_PERFORMED: 'event::hanging_protocol_custom_image_load_performed',
  };

  public static REGISTRATION = {
    name: 'hangingProtocolService',
    altName: 'HangingProtocolService',
    create: ({ configuration = {}, commandsManager, servicesManager }) => {
      return new HangingProtocolService(commandsManager, servicesManager);
    },
  };

  studies: StudyMetadata[];
  // stores all the protocols (object or function that returns an object) in a map
  protocols: Map<string, Protocol>;
  // Contains the list of currently active keys
  activeProtocolIds: string[];
  // the current protocol that is being applied to the viewports in object format
  protocol: HangingProtocol.Protocol;
  // The version of the protocol that must not be modified with customizations
  // if it was defined in the protocol definition. This is a copy of the protocol
  // that is used to recompute the computedOptions when necessary as we override
  // the computedOptions in the protocol object itself.
  _originalProtocol: HangingProtocol.Protocol;

  stageIndex = 0;
  _commandsManager: CommandsManager;
  _servicesManager: AppTypes.ServicesManager;
  protocolEngine: ProtocolEngine;
  customViewportSettings = [];
  displaySets: DisplaySet[] = [];
  activeStudy: StudyMetadata;
  debugLogging: false;

  customAttributeRetrievalCallbacks = {
    NumberOfStudyRelatedSeries: {
      name: 'The number of series in the study',
      callback: metadata => metadata.NumberOfStudyRelatedSeries ?? metadata.series?.length,
    },
    NumberOfSeriesRelatedInstances: {
      name: 'The number of instances in the display set',
      callback: metadata => metadata.numImageFrames,
    },
    ModalitiesInStudy: {
      name: 'Gets the array of the modalities for the series',
      callback: metadata =>
        metadata.ModalitiesInStudy ??
        (metadata.series || []).reduce((prev, curr) => {
          const { Modality } = curr;
          if (Modality && prev.indexOf(Modality) == -1) {
            prev.push(Modality);
          }
          return prev;
        }, []),
    },
    isReconstructable: {
      name: 'Checks if the display set is reconstructable',
      // we can add more advanced checking here
      callback: displaySet => displaySet.isReconstructable ?? false,
    },
    isDisplaySetFromUrl: {
      name: 'Checks if the display set is as specified in the URL',
      callback: isDisplaySetFromUrl,
    },
    sopInstanceLocation: {
      name: 'Gets the position of the specified sop instance',
      callback: sopInstanceLocation,
    },
    seriesDescriptions: {
      name: 'seriesDescriptions',
      description: 'List of Series Descriptions',
      callback: seriesDescriptionsFromDisplaySets,
    },
    numberOfDisplaySetsWithImages: {
      name: 'numberOfDisplaySetsWithImages',
      description: 'Number of displays sets with images',
      callback: numberOfDisplaySetsWithImages,
    },
  };
  listeners = {};
  registeredImageLoadStrategies = {};
  activeImageLoadStrategyName = null;
  customImageLoadPerformed = false;

  /**
   * displaySetMatchDetails = <displaySetId, match>
   * DisplaySetId is the id defined in the hangingProtocol object itself
   * and match is an object that contains information about
   */
  displaySetMatchDetails: Map<
    string, // protocol displaySetId in the displayset selector
    HangingProtocol.DisplaySetMatchDetails
  > = new Map();

  /**
   * An array that contains for each viewport (viewportId) specified in the
   * hanging protocol, an object of the form
   */
  viewportMatchDetails: Map<
    string, // viewportId
    HangingProtocol.ViewportMatchDetails
  > = new Map();

  constructor(commandsManager: CommandsManager, servicesManager: AppTypes.ServicesManager) {
    super(HangingProtocolService.EVENTS);
    this._commandsManager = commandsManager;
    this._servicesManager = servicesManager;
    this.protocols = new Map();
    this.protocolEngine = undefined;
    this.protocol = undefined;
    this.stageIndex = undefined;

    this.studies = [];
  }

  public destroy(): void {
    this.reset();
    this.protocols = new Map();
  }

  public reset(): void {
    this.studies = [];
    this.viewportMatchDetails = new Map();
    this.displaySetMatchDetails = new Map();
    this.protocol = undefined;
    this.stageIndex = undefined;
    this.protocolEngine = undefined;
  }

  /** Leave the hanging protocol in the initialized state */
  public onModeEnter(): void {
    this.reset();
  }

  /**
   * Gets the active protocol information directly, including the direct
   * protocol, stage and active study objects.
   * Should NOT be stored longer term as the protocol
   * object can change internally or be regenerated.
   * Can be used to store the state to recover from exceptions.
   *
   * @returns protocol, stage, activeStudy
   */
  public getActiveProtocol(): {
    protocol: HangingProtocol.Protocol;
    _originalProtocol: HangingProtocol.Protocol;
    stage: HangingProtocol.ProtocolStage;
    stageIndex: number;
    activeStudy?: StudyMetadata;
    viewportMatchDetails: Map<string, HangingProtocol.ViewportMatchDetails>;
    displaySetMatchDetails: Map<string, HangingProtocol.DisplaySetMatchDetails>;
    activeImageLoadStrategyName: string;
  } {
    return {
      protocol: this.protocol,
      _originalProtocol: this._originalProtocol,
      stage: this.protocol?.stages?.[this.stageIndex],
      stageIndex: this.stageIndex,
      activeStudy: this.activeStudy,
      viewportMatchDetails: this.viewportMatchDetails,
      displaySetMatchDetails: this.displaySetMatchDetails,
      activeImageLoadStrategyName: this.activeImageLoadStrategyName,
    };
  }

  /** Gets the hanging protocol state information, which is a storable
   * state information for the hanging protocol consisting of the:
   * protocolId, stageIndex, stageId and activeStudyUID
   */
  public getState(): HangingProtocol.HPInfo {
    if (!this.protocol) {
      return;
    }
    return {
      protocolId: this.protocol.id,
      stageIndex: this.stageIndex,
      stageId: this.protocol.stages[this.stageIndex].id,
      activeStudyUID: this.activeStudy?.StudyInstanceUID,
    };
  }

  /**
   * Filters the series required for running a hanging protocol.
   *
   * This can be extended in the future with more complex selection rules e.g.
   * N series of a given type, and M of a different type, such as all CT series,
   * and all SR, and then everything else.
   *
   * @param protocolId - The ID of the hanging protocol.
   * @param seriesPromises - An array of promises representing the series.
   * @returns An object containing the required series and the remaining series.
   */
  public filterSeriesRequiredForRun(protocolId, seriesPromises) {
    if (Array.isArray(protocolId)) {
      protocolId = protocolId[0];
    }
    const minSeriesLoadedToRunHP =
      this.getProtocolById(protocolId)?.hpInitiationCriteria?.minSeriesLoaded ||
      seriesPromises.length;
    const requiredSeries = seriesPromises.slice(0, minSeriesLoadedToRunHP);
    const remaining = seriesPromises.slice(minSeriesLoadedToRunHP);
    return {
      requiredSeries,
      remaining,
    };
  }

  /** Gets the protocol with id 'default' */
  public getDefaultProtocol(): HangingProtocol.Protocol {
    return this.getProtocolById('default');
  }

  /** Gets the viewport match details.
   * @deprecated because this method is expected to go away as the HP service
   *    becomes more stateless.
   */
  public getMatchDetails(): HangingProtocol.HangingProtocolMatchDetails {
    return {
      viewportMatchDetails: this.viewportMatchDetails,
      displaySetMatchDetails: this.displaySetMatchDetails,
    };
  }

  /**
   * It loops over the protocols map object, and checks whether the protocol
   * is a function, if so, it executes it and returns the result as a protocol object
   * otherwise it returns the protocol object itself
   *
   * @returns all the hanging protocol registered in the HangingProtocolService
   */
  public getProtocols(): HangingProtocol.Protocol[] {
    // this.protocols is a map of protocols with the protocol id as the key
    // and the protocol or a function that returns a protocol as the value
    const protocols = [];
    const keys = this.activeProtocolIds || this.protocols.keys();
    for (const protocolId of keys) {
      const protocol = this.getProtocolById(protocolId);
      if (protocol) {
        protocols.push(protocol);
      }
    }

    return protocols;
  }

  /**
   * Returns the protocol with the given id, it will get the protocol from the
   * protocols map object and if it is a function, it will execute it and return
   * the result as a protocol object
   *
   * @param protocolId - the id of the protocol
   * @returns protocol - the protocol with the given id
   */
  public getProtocolById(protocolId: string, caseInsensitive = true): HangingProtocol.Protocol {
    if (!protocolId) {
      return;
    }
    if (protocolId === this.protocol?.id) {
      return this.protocol;
    }

    let protocol = this.protocols.get(protocolId);
    if (!protocol && caseInsensitive) {
      const lowerCaseId = protocolId.toLowerCase();
      for (const [key] of this.protocols) {
        if (key.toLowerCase() === lowerCaseId) {
          protocol = this.getProtocolById(key);
          break;
        }
      }
    }

    if (!protocol) {
      throw new Error(`No protocol ${protocolId} found`);
    }

    if (protocol instanceof Function) {
      try {
        const { protocol: generatedProtocol } = this._getProtocolFromGenerator(protocol);

        return generatedProtocol;
      } catch (error) {
        console.warn(
          `Error while executing protocol generator for protocol ${protocolId}: ${error}`
        );
      }
    } else {
      return this._validateProtocol(protocol);
    }
  }

  /**
   * It adds a protocol to the protocols map object. If a protocol with the given
   * id already exists, warn the user and overwrite it.  This can be used to
   * set a new "default" protocol.
   *
   * @param {string} protocolId - The id of the protocol.
   * @param {Protocol} protocol - Protocol - This is the protocol that you want to
   * add to the protocol manager.
   */
  public addProtocol(protocolId: string, protocol: Protocol): void {
    if (this.protocols.has(protocolId)) {
      console.warn(`A protocol with id ${protocolId} already exists. It will be overwritten.`);
    }

    if (!(protocol instanceof Function)) {
      protocol = this._validateProtocol(protocol as HangingProtocol.Protocol);
    }

    this.protocols.set(protocolId, protocol);
  }

  /**
   * Add a given protocol object as active.
   * If active protocols ids is null right now, then the specified
   * protocol will become the only active protocol.
   */
  public addActiveProtocolId(id: string): void {
    if (!id) {
      return;
    }
    if (!this.activeProtocolIds) {
      this.activeProtocolIds = [];
    }
    this.activeProtocolIds.push(id);
  }

  /**
   * Sets the active hanging protocols to use, by name.  If the value is empty,
   * then resets the active protocols to all the named items.
   */
  public setActiveProtocolIds(protocolId?: string[] | string): void {
    if (!protocolId || !protocolId.length) {
      this.activeProtocolIds = null;
      /* eslint-disable */console.log(...oo_oo(`1204721250_369_6_369_63_4`,'No active protocols, setting all to active'));
      return;
    }
    if (typeof protocolId === 'string') {
      this.setActiveProtocolIds([protocolId]);
      return;
    }
    this.activeProtocolIds = [...protocolId];
  }

  /**
   * Sets the active study.
   * This is the study that the hanging protocol will consider active and
   * may or may not be the study that is being shown by the protocol currently,
   * for example, a prior view hanging protocol will NOT show the active study
   * specifically, but will show another study instead.
   */
  public setActiveStudyUID(activeStudyUID: string) {
    if (!activeStudyUID || activeStudyUID === this.activeStudy?.StudyInstanceUID) {
      return;
    }
    this.activeStudy = this.studies.find(it => it.StudyInstanceUID === activeStudyUID);
    return this.activeStudy;
  }

  public hasStudyUID(studyUID: string): boolean {
    return this.studies.some(it => it.StudyInstanceUID === studyUID);
  }

  public addStudy(study) {
    if (!this.hasStudyUID(study.StudyInstanceUID)) {
      this.studies.push(study);
    }
  }

  /**
   * Run the hanging protocol decisions tree on the active study,
   * studies list and display sets, firing a PROTOCOL_CHANGED event when
   * complete to indicate the hanging protocol is ready, and which stage
   * got applied/activated.
   *
   * Also fires a STAGES_ACTIVE event to indicate which stages are able to be
   * activated.
   *
   * @param params is the dataset to run the hanging protocol on.
   * @param params.activeStudy is the "primary" study to hang  This may or may
   *        not be displayed by the actual viewports.
   * @param params.studies is the list of studies to hang.  If absent, will reuse the previous set.
   * @param params.displaySets is the list of display sets associated with
   *        the studies to display in viewports.
   * @param protocol is a specific protocol to apply.
   */
  public run({ studies, displaySets, activeStudy }, protocolId, options = {}) {
    this.studies = [...(studies || this.studies)];
    this.displaySets = displaySets;
    this.setActiveStudyUID(
      activeStudy?.StudyInstanceUID || (activeStudy || this.studies[0])?.StudyInstanceUID
    );

    this.protocolEngine = new ProtocolEngine(
      this.getProtocols(),
      this.customAttributeRetrievalCallbacks
    );

    // Resets the full protocol status here.
    this.protocol = null;

    if (protocolId && typeof protocolId === 'string') {
      const protocol = this.getProtocolById(protocolId);
      this._setProtocol(protocol, options);
    } else {
      const matchedProtocol = this.protocolEngine.run({
        studies: this.studies,
        activeStudy,
        displaySets,
      });
      this._setProtocol(matchedProtocol);
    }

    if (this.protocol?.callbacks?.onProtocolEnter) {
      this._commandsManager.run(this.protocol?.callbacks?.onProtocolEnter);
    }
  }

  /**
   * Returns true, if the hangingProtocol has a custom loading strategy for the images
   * and its callback has been added to the HangingProtocolService
   * @returns A boolean indicating whether a custom image load strategy has been added or not.
   */
  public hasCustomImageLoadStrategy(): boolean {
    return (
      this.activeImageLoadStrategyName !== null &&
      this.registeredImageLoadStrategies[this.activeImageLoadStrategyName] instanceof Function
    );
  }

  /**
   * Returns a boolean indicating whether a custom image load has been performed or not.
   * A custom image load is performed when a custom image load strategy is used to load images.
   * This method is used internally by the HangingProtocolService to determine whether to perform
   * a custom image load or not.
   *
   * @returns A boolean indicating whether a custom image load has been performed or not.
   */
  private getCustomImageLoadPerformed(): boolean {
    return this.customImageLoadPerformed;
  }

  /**
   * Returns a boolean indicating whether a custom image load should be performed or not.
   * A custom image load should be performed if a custom image load strategy has been added to the HangingProtocolService
   * and it has not been performed yet.
   *
   * @returns A boolean indicating whether a custom image load should be performed or not.
   */
  public getShouldPerformCustomImageLoad(): boolean {
    return this.hasCustomImageLoadStrategy() && !this.getCustomImageLoadPerformed();
  }

  /**
   * Set the strategy callback for loading images to the HangingProtocolService
   * @param {string} name strategy name
   * @param {Function} callback image loader callback
   */
  public registerImageLoadStrategy(name, callback): void {
    if (callback instanceof Function && name) {
      this.registeredImageLoadStrategies[name] = callback;
    }
  }

  /**
   * Adds a custom attribute to be used in the HangingProtocol UI and matching rules, including a
   * callback that will be used to calculate the attribute value.
   *
   * @param attributeId The ID used to refer to the attribute (e.g. 'timepointType')
   * @param attributeName The name of the attribute to be displayed (e.g. 'Timepoint Type')
   * @param callback The function used to calculate the attribute value from the other attributes at its level (e.g. study/series/image)
   * @param options to add to the "this" object for the custom attribute retriever
   */
  public addCustomAttribute(
    attributeId: string,
    attributeName: string,
    callback: (metadata: Record<string, unknown>, extraData?: Record<string, unknown>) => unknown,
    options: Record<string, unknown> = {}
  ): void {
    this.customAttributeRetrievalCallbacks[attributeId] = {
      ...options,
      id: attributeId,
      name: attributeName,
      callback,
    };
  }

  /**
   * Executes the callback function for the custom loading strategy for the images
   * if no strategy is set, the default strategy is used
   */
  runImageLoadStrategy(data): boolean {
    const loader = this.registeredImageLoadStrategies[this.activeImageLoadStrategyName];
    const loadedData = loader({
      data,
      displaySetsMatchDetails: this.displaySetMatchDetails,
      viewportMatchDetails: this.viewportMatchDetails,
    });

    // if loader successfully re-arranged the data with the custom strategy
    // and returned the new props, then broadcast them
    if (!loadedData) {
      console.warn('Not able to load data with custom strategy');
      return false;
    }

    this.customImageLoadPerformed = true;
    this._broadcastEvent(this.EVENTS.CUSTOM_IMAGE_LOAD_PERFORMED, loadedData);
    return true;
  }

  _validateProtocol(protocol: HangingProtocol.Protocol): HangingProtocol.Protocol {
    protocol.name = protocol.name ?? protocol.id;
    const { stages } = protocol;

    if (!stages) {
      console.warn('Protocol has not stages:', protocol.id, protocol);
      return;
    }

    for (const id of Object.keys(protocol.displaySetSelectors)) {
      const selector = protocol.displaySetSelectors[id];
      selector.id = id;
      const { seriesMatchingRules } = selector;
      if (!seriesMatchingRules) {
        console.warn('Selector has no series matching rules', protocol.id, id);
        return;
      }
    }

    // Generate viewports automatically as required.
    stages.forEach(stage => {
      if (!stage.viewports) {
        stage.name = stage.name || stage.id;
        stage.viewports = [];
        const { rows, columns } = stage.viewportStructure.properties;

        for (let i = 0; i < rows * columns; i++) {
          const defaultViewport = stage.defaultViewport || protocol.defaultViewport || {};
          stage.viewports.push({
            viewportOptions: {
              ...defaultViewport.viewportOptions,
              viewportId: i === 0 ? 'default' : uuidv4(),
            },
            displaySets: [],
          });
        }
      } else {
        // Clone each viewport to ensure independent objects
        stage.viewports = stage.viewports.map((viewport, index) => {
          const defaultViewport = stage.defaultViewport || protocol.defaultViewport || {};
          const existingViewportId = viewport.viewportOptions?.viewportId;

          return {
            ...viewport,
            viewportOptions: {
              ...defaultViewport.viewportOptions,
              ...viewport.viewportOptions,
              viewportId: existingViewportId
                ? existingViewportId
                : index === 0
                  ? 'default'
                  : uuidv4(),
            },
            displaySets: viewport.displaySets || [],
          };
        });
        stage.viewports.forEach(viewport => {
          viewport.displaySets.forEach(displaySet => {
            displaySet.options = displaySet.options || {};
          });
        });
      }
    });

    return protocol;
  }

  private _getProtocolFromGenerator(protocolGenerator: HangingProtocol.ProtocolGenerator): {
    protocol: HangingProtocol.Protocol;
  } {
    const { protocol } = protocolGenerator({
      servicesManager: this._servicesManager,
      commandsManager: this._commandsManager,
    });

    const validatedProtocol = this._validateProtocol(protocol);

    return {
      protocol: validatedProtocol,
    };
  }

  /**
   * This will return the viewports that need to be updated based on the
   * hanging protocol layout and the displaySetInstanceUID that needs to be updated.
   *
   * This is useful, when for instance we drag and drop a displaySet into a viewport
   * which is in MPR, and we need to update the other viewports that are showing the same
   * layout.
   *
   * However, sometimes since we get out of sync with the hanging protocol layout, when
   * the user use the custom grid layout, we should not update the other viewports, and that is
   * when the isHangingProtocolLayout is set to false.
   *
   * @param viewportId - the id of the viewport that needs to be updated
   * @param displaySetInstanceUID - the displaySetInstanceUID that needs to be updated
   * @param isHangingProtocolLayout - whether the layout is a hanging protocol layout
   * @returns
   */
  getViewportsRequireUpdate(viewportId, displaySetInstanceUID, isHangingProtocolLayout = true) {
    const newDisplaySetInstanceUID = displaySetInstanceUID;
    const defaultReturn = [
      {
        viewportId,
        displaySetInstanceUIDs: [newDisplaySetInstanceUID],
      },
    ];

    if (!isHangingProtocolLayout) {
      return defaultReturn;
    }

    const { displaySetService } = this._servicesManager.services;
    const displaySet = displaySetService.getDisplaySetByUID(displaySetInstanceUID);
    if (displaySet?.unsupported) {
      throw new Error('Unsupported displaySet');
    }
    const protocol = this.protocol;
    const protocolStage = protocol.stages[this.stageIndex];
    const protocolViewports = protocolStage.viewports;

    if (!protocolViewports) {
      return defaultReturn;
    }

    const protocolViewport = protocolViewports.find(
      pv => pv.viewportOptions.viewportId === viewportId
    );

    // if no viewport, then we can assume there is no predefined set of
    // rules that should be applied to this viewport while matching
    if (!protocolViewport) {
      return defaultReturn;
    }

    // no support for drag and drop into fusion viewports yet
    // Todo: smart drag and drop would look at the displaySets and
    // replace the same modality type, but later
    if (protocolViewport.displaySets.length > 1) {
      throw new Error('Cannot update viewport with multiple displaySets yet');
    }

    // If there is no displaySet, then we can assume that the viewport
    // is empty and we can just add the new displaySet to it
    if (protocolViewport.displaySets.length === 0) {
      return defaultReturn;
    }

    // If the viewport options says to allow any instance, then we can assume
    // it just updates this viewport.  This is deprecated and will be removed
    if (protocolViewport.viewportOptions.allowUnmatchedView) {
      return defaultReturn;
    }

    // if the viewport is not empty, then we check the displaySets it is showing
    // currently, which means we need to check if the requested updated displaySet
    // follow the same rules as the current displaySets
    const { id: displaySetSelectorId, matchedDisplaySetsIndex = 0 } =
      protocolViewport.displaySets[0];
    const displaySetSelector = protocol.displaySetSelectors[displaySetSelectorId];

    // The display set can allow any view
    if (!displaySetSelector || displaySetSelector.allowUnmatchedView) {
      return defaultReturn;
    }

    // so let's check if the new displaySetInstanceUIDs follow the same rules
    this._validateViewportSpecificMatch(
      {
        displaySetInstanceUIDs: [newDisplaySetInstanceUID],
        viewportOptions: {},
        displaySetOptions: [],
      },
      protocolViewport,
      protocol.displaySetSelectors
    );
    // if we reach here, it means there are some rules that should be applied

    // if we don't have any match details for the displaySetSelector the viewport
    // is currently showing, then we can assume that the new displaySetInstanceUID
    // does not
    if (!this.displaySetMatchDetails.get(displaySetSelectorId)) {
      return defaultReturn;
    }

    const originalProtocol = this._originalProtocol;
    let originalProtocolStage;
    if (!(originalProtocol instanceof Function)) {
      originalProtocolStage = originalProtocol.stages[this.stageIndex];
    }

    // if we reach here, it means that the displaySetInstanceUIDs to be dropped
    // for the desired viewportId are valid, and we can proceed with the update. However
    // we need to check if the displaySets that the viewport were showing
    // was also referenced by other viewports, and if so, we need to update those
    // viewports as well

    // check if displaySetSelectors are used by other viewports, and
    // store the viewportId and displaySetInstanceUIDs that need to be updated
    const viewportsToUpdate = [];
    protocolViewports.forEach((viewport, index) => {
      const viewportNeedsUpdate = viewport.displaySets.some(
        displaySet =>
          displaySet.id === displaySetSelectorId &&
          (displaySet.matchedDisplaySetsIndex || 0) === matchedDisplaySetsIndex
      );

      if (viewportNeedsUpdate) {
        // Try to recompute the viewport options based on the current
        // viewportId that needs update but from its old/original un-computed
        // viewport & displaySet options
        if (originalProtocolStage) {
          const originalViewport = originalProtocolStage.viewports[index];
          const originalViewportOptions = originalViewport.viewportOptions;
          const originalDisplaySetOptions = originalViewport.displaySets;

          viewport.viewportOptions = this.getComputedOptions(originalViewportOptions, [
            newDisplaySetInstanceUID,
          ]);

          viewport.displaySets = this.getComputedOptions(originalDisplaySetOptions, [
            newDisplaySetInstanceUID,
          ]);
        }

        const displaySetInstanceUIDs = [];
        const displaySetOptions = [];

        this._updateDisplaySetInstanceUIDs(
          viewport,
          displaySetSelectorId,
          newDisplaySetInstanceUID,
          this.displaySetMatchDetails,
          displaySetInstanceUIDs,
          displaySetOptions
        );

        viewportsToUpdate.push({
          viewportId: viewport.viewportOptions.viewportId,
          displaySetInstanceUIDs,
          viewportOptions: viewport.viewportOptions,
          displaySetOptions,
        });
      }
    });

    return viewportsToUpdate;
  }

  public runMatchingRules(metadataArray, matchingRules, options) {
    return this.protocolEngine.findMatch(metadataArray, matchingRules, options);
  }

  private _updateDisplaySetInstanceUIDs(
    viewport: HangingProtocol.Viewport,
    displaySetSelectorId: string,
    newDisplaySetInstanceUID: string,
    displaySetMatchDetails: Map<string, HangingProtocol.DisplaySetMatchDetails>,
    displaySetInstanceUIDs: string[],
    displaySetOptions: HangingProtocol.DisplaySetOptions[]
  ) {
    viewport.displaySets.forEach(displaySet => {
      const { id } = displaySet;
      const displaySetMatchDetail = displaySetMatchDetails.get(id);

      const { displaySetInstanceUID: oldDisplaySetInstanceUID } = displaySetMatchDetail;

      const displaySetInstanceUID =
        displaySet.id === displaySetSelectorId
          ? newDisplaySetInstanceUID
          : oldDisplaySetInstanceUID;

      displaySetMatchDetail.displaySetInstanceUID = displaySetInstanceUID;

      displaySetInstanceUIDs.push(displaySetInstanceUID);
      displaySetOptions.push(displaySet);
    });
  }

  /**
   *  Gets a computed options value, or a copy of the options
   * This allows computing values such as the initial image index to use
   * based on custom attribute functions, the same as the validators.
   * Computing individual values is something that can be declared statically
   * as long as the named functions are provided ahead of time, which is much
   * simpler than recomputing the entire protocol.
   */
  public getComputedOptions(
    options: Record<string, unknown> | Array<Record<string, unknown>>,
    displaySetUIDs: string[]
  ): any {
    // Base case: if options is an array, map over the array and recursively call getComputedOptions
    if (Array.isArray(options)) {
      return options.map(option => this.getComputedOptions(option, displaySetUIDs));
    }

    if (options === null) {
      return options;
    }
    if (typeof options !== 'object') {
      return options;
    }

    // If options is an object with a custom attribute, compute a new options object
    if (options.custom) {
      const displaySets = this.displaySets.filter(displaySet =>
        displaySetUIDs.includes(displaySet.displaySetInstanceUID)
      );

      const customKey = options.custom as string;
      if (!(customKey in this.customAttributeRetrievalCallbacks)) {
        throw new Error(
          `Custom key "${customKey}" not found in customAttributeRetrievalCallbacks.`
        );
      }

      const callback = this.customAttributeRetrievalCallbacks[customKey].callback;
      let newOptions = callback.call(options, displaySets);

      if (newOptions === undefined) {
        newOptions = options.defaultValue;
      }

      return this.getComputedOptions(newOptions, displaySetUIDs);
    }

    // If options is an object without a custom attribute, recursively call getComputedOptions on its properties
    const newOptions = {} as Record<string, unknown>;
    for (const key in options) {
      // if not undefined
      if (options[key] !== undefined) {
        newOptions[key] = this.getComputedOptions(options[key], displaySetUIDs);
      }
    }

    return newOptions;
  }

  /**
   * It applied the protocol to the current studies and display sets based on the
   * protocolId that is provided.
   * @param protocolId - name of the registered protocol to be set
   * @param options - options to be passed to the protocol, this is either an array
   * of the displaySetInstanceUIDs to be set on ALL VIEWPORTS OF THE PROTOCOL or an object
   * that contains viewportId as the key and displaySetInstanceUIDs as the value
   * for each viewport that needs to be set.
   * @param errorCallback - callback to be called if there is an error
   * during the protocol application
   *
   * @returns boolean - true if the protocol was applied and no errors were found
   */
  public setProtocol(
    protocolId: string,
    options = {} as HangingProtocol.SetProtocolOptions,
    errorCallback = null
  ): void {
    const foundProtocol = this.protocols.get(protocolId);

    if (!foundProtocol) {
      console.warn(
        `ProtocolEngine::setProtocol - Protocol with id ${protocolId} not found - you should register it first via addProtocol`
      );
      return;
    }

    try {
      const protocol = this._validateProtocol(foundProtocol);

      if (options) {
        this._validateOptions(options);
      }

      this._setProtocol(protocol, options);
    } catch (error) {
      /* eslint-disable */console.log(...oo_oo(`1204721250_920_6_920_24_4`,error));

      if (errorCallback) {
        errorCallback(error);
      }

      throw new Error(error);
    }

    this._commandsManager.run(this.protocol?.callbacks?.onProtocolEnter);
  }

  protected matchActivation(
    matchedViewports: number,
    activation: HangingProtocol.StageActivation = {},
    minViewportsMatched: number
  ): boolean {
    const { displaySetSelectors } = this.protocol;

    const { displaySetSelectorsMatched = [] } = activation;
    for (const dsName of displaySetSelectorsMatched) {
      const displaySetSelector = displaySetSelectors[dsName];
      if (!displaySetSelector) {
        console.warn('No display set selector for', dsName);
        return false;
      }
      const { bestMatch } = this._matchImages(displaySetSelector);
      if (!bestMatch) {
        return false;
      }
    }
    const min = activation.minViewportsMatched ?? minViewportsMatched;

    return matchedViewports >= min;
  }
  /**
   * Updates the stage activation, setting the stageActivation values to
   * 'disabled', 'active', 'passive' where:
   * * disabled means there are insufficient viewports filled to show this
   * * passive means there aren't enough preferred viewports filled to show
   * this stage by default, but it can be manually selected
   * * enabled means there are enough viewports to select this viewport by default
   *
   * The logic is currently simple, just count how many viewports would be
   * filled, and compare to the required/preferred count, but the intent is
   * to allow more complex rules in the future as required.
   *
   * @returns the stage number to apply initially, given the options.
   */
  private _updateStageStatus(options = null as HangingProtocol.SetProtocolOptions) {
    const stages = this.protocol.stages;
    for (let i = 0; i < stages.length; i++) {
      const stage = stages[i];

      const { matchedViewports } = this._matchAllViewports(stage, options, new Map());
      const activation = stage.stageActivation || {};
      if (this.matchActivation(matchedViewports, activation.passive, 0)) {
        if (this.matchActivation(matchedViewports, activation.enabled, 1)) {
          stage.status = 'enabled';
        } else {
          stage.status = 'passive';
        }
      } else {
        stage.status = 'disabled';
      }
    }

    this._broadcastEvent(this.EVENTS.STAGE_ACTIVATION, {
      protocol: this.protocol,
      stages: this.protocol.stages,
    });
  }

  private _findStageIndex(options = null as HangingProtocol.SetProtocolOptions): number | void {
    const stageId = options?.stageId;
    const protocol = this.protocol;
    const stages = protocol.stages;

    if (stageId) {
      for (let i = 0; i < stages.length; i++) {
        const stage = stages[i];
        if (stage.id === stageId && stage.status !== 'disabled') {
          return i;
        }
      }
      return;
    }

    const stageIndex = options?.stageIndex;
    if (stageIndex !== undefined) {
      return stages[stageIndex]?.status !== 'disabled' ? stageIndex : undefined;
    }

    let firstNotDisabled: number;

    for (let i = 0; i < stages.length; i++) {
      if (stages[i].status === 'enabled') {
        return i;
      }
      if (firstNotDisabled === undefined && stages[i].status !== 'disabled') {
        firstNotDisabled = i;
      }
    }

    return firstNotDisabled;
  }

  private _setProtocol(
    protocol: HangingProtocol.Protocol,
    options = null as HangingProtocol.SetProtocolOptions
  ): void {
    const old = this.getActiveProtocol();

    try {
      if (!this.protocol || this.protocol.id !== protocol.id) {
        this.stageIndex = options?.stageIndex || 0;
        //Reset load performed to false to re-fire loading strategy at new study opening
        this.customImageLoadPerformed = false;
        this._originalProtocol = this._copyProtocol(protocol);

        // before reassigning the protocol, we need to check if there is a callback
        // on the old protocol that needs to be called
        // Send the notification about updating the state
        if (this.protocol?.callbacks?.onProtocolExit) {
          this._commandsManager.run(this.protocol.callbacks.onProtocolExit);
        }

        this.protocol = protocol;

        const { imageLoadStrategy } = protocol;
        if (imageLoadStrategy) {
          // check if the imageLoadStrategy is a valid strategy
          if (this.registeredImageLoadStrategies[imageLoadStrategy] instanceof Function) {
            this.activeImageLoadStrategyName = imageLoadStrategy;
          }
        } else {
          this.activeImageLoadStrategyName = null;
        }

        this._updateStageStatus(options);
      }

      const stage = this._findStageIndex(options);
      if (stage === undefined) {
        throw new Error(`Can't find applicable stage ${protocol.id} ${options?.stageIndex}`);
      }
      this.stageIndex = stage as number;

      if (this.protocol?.callbacks?.onStageChange) {
        this._commandsManager.run(this.protocol.callbacks.onStageChange);
      }

      this._updateViewports(options);
    } catch (error) {
      /* eslint-disable */console.log(...oo_oo(`1204721250_1074_6_1074_24_4`,error));
      Object.assign(this, old);
      throw new Error(error);
    }

    if (options?.restoreProtocol !== true) {
      this._broadcastEvent(HangingProtocolService.EVENTS.PROTOCOL_CHANGED, {
        viewportMatchDetails: this.viewportMatchDetails,
        displaySetMatchDetails: this.displaySetMatchDetails,
        protocol: this.protocol,
        stageIdx: this.stageIndex,
        stage: this.protocol.stages[this.stageIndex],
        activeStudyUID: this.activeStudy?.StudyInstanceUID,
      });
    } else {
      this._broadcastEvent(HangingProtocolService.EVENTS.PROTOCOL_RESTORED, {
        protocol: this.protocol,
        stageIdx: this.stageIndex,
        stage: this.protocol.stages[this.stageIndex],
        activeStudyUID: this.activeStudy?.StudyInstanceUID,
      });
    }
  }

  public getStageIndex(protocolId: string, options): number {
    const protocol = this.getProtocolById(protocolId);
    const { stageId, stageIndex } = options;
    if (stageId !== undefined) {
      return protocol.stages.findIndex(it => it.id === stageId);
    }
    if (stageIndex !== undefined) {
      return stageIndex;
    }
    return 0;
  }

  /**
   * Retrieves the number of Stages in the current Protocol or
   * undefined if no protocol or stages are set
   */
  _getNumProtocolStages() {
    if (!this.protocol || !this.protocol.stages || !this.protocol.stages.length) {
      return;
    }

    return this.protocol.stages.length;
  }

  /**
   * Retrieves the current Stage from the current Protocol and stage index
   *
   * @returns {*} The Stage model for the currently displayed Stage
   */
  _getCurrentStageModel() {
    return this.protocol.stages[this.stageIndex];
  }

  /**
   * Gets a new viewport object for missing viewports.  Used to fill
   * new viewports.
   * Looks first for the stage, to see if there is a missingViewport defined,
   * and secondly looks to the overall protocol.
   *
   * Returns a matchInfo object, which can be used to create the actual
   * viewport object (which this class knows nothing about).
   */
  public getMissingViewport(
    protocolId: string,
    stageIdx: number,
    options
  ): HangingProtocol.ViewportMatchDetails {
    if (this.protocol.id !== protocolId) {
      console.warn('setting protocol');
      this.protocol = this.getProtocolById(protocolId);
      this.stageIndex = 0;
    }
    const protocol = this.protocol;
    const stage = protocol.stages[stageIdx] ?? protocol.stages[this.stageIndex];
    const defaultViewport = stage.defaultViewport || protocol.defaultViewport;
    if (!defaultViewport) {
      return;
    }

    const useViewport = { ...defaultViewport };
    return this._matchViewport(useViewport, options);
  }

  /**
   * Gets a sort function that is consistent with the display set sorting performed
   * to match display sets to viewports.
   * @returns a display set sort function
   */
  public getDisplaySetSortFunction(): (displaySetA: DisplaySet, displaySetB: DisplaySet) => number {
    return (displaySetA, displaySetB) => {
      const seriesA = this._getSeriesSortInfoForDisplaySetSort(displaySetA);
      const seriesB = this._getSeriesSortInfoForDisplaySetSort(displaySetB);

      return sortBy(this._getSeriesFieldForDisplaySetSort())(seriesA, seriesB);
    };
  }

  /**
   * Updates the viewports with the selected protocol stage.
   */
  _updateViewports(options = null as HangingProtocol.SetProtocolOptions): void {
    // Make sure we have an active protocol with a non-empty array of display sets
    if (!this._getNumProtocolStages()) {
      throw new Error('No protocol or stages found');
    }

    // each time we are updating the viewports, we need to reset the
    // matching applied
    this.viewportMatchDetails = new Map();
    this.displaySetMatchDetails = new Map();

    // Retrieve the current stage
    const stageModel = this._getCurrentStageModel();

    // If the current stage does not fulfill the requirements to be displayed,
    // stop here.
    if (
      !stageModel ||
      !stageModel.viewportStructure ||
      !stageModel.viewports ||
      !stageModel.viewports.length
    ) {
      /* eslint-disable */console.log(...oo_oo(`1204721250_1200_6_1200_56_4`,'Stage cannot be applied', stageModel));
      return;
    }

    const { layoutType } = stageModel.viewportStructure;
    // Retrieve the properties associated with the current display set's viewport structure template
    // If no such layout properties exist, stop here.
    const layoutProps = stageModel.viewportStructure.properties;
    if (!layoutProps) {
      /* eslint-disable */console.log(...oo_oo(`1204721250_1209_6_1209_67_4`,'No viewportStructure.properties in', stageModel));
      return;
    }

    const { columns: numCols, rows: numRows, layoutOptions = [] } = layoutProps;

    if (this.protocol?.callbacks?.onViewportDataInitialized) {
      this._commandsManager.runCommand('attachProtocolViewportDataListener', {
        protocol: this.protocol,
        stageIndex: this.stageIndex,
      });
    }

    this._broadcastEvent(this.EVENTS.NEW_LAYOUT, {
      layoutType,
      numRows,
      numCols,
      layoutOptions,
    });

    // Loop through each viewport
    this._matchAllViewports(this.protocol.stages[this.stageIndex], options);
  }

  private _matchAllViewports(
    stageModel: HangingProtocol.ProtocolStage,
    options?: HangingProtocol.SetProtocolOptions,
    viewportMatchDetails = this.viewportMatchDetails,
    displaySetMatchDetails = this.displaySetMatchDetails
  ): {
    matchedViewports: number;
    viewportMatchDetails: Map<string, HangingProtocol.ViewportMatchDetails>;
    displaySetMatchDetails: Map<string, HangingProtocol.DisplaySetMatchDetails>;
  } {
    this.activeStudy ||= this.studies[0];
    let matchedViewports = 0;
    stageModel.viewports.forEach(viewport => {
      const viewportId = viewport.viewportOptions.viewportId;
      const matchDetails = this._matchViewport(
        viewport,
        options,
        viewportMatchDetails,
        displaySetMatchDetails
      );
      if (matchDetails) {
        if (
          matchDetails.displaySetsInfo?.length &&
          matchDetails.displaySetsInfo[0].displaySetInstanceUID
        ) {
          matchedViewports++;
        } else {
          /* eslint-disable */console.log(...oo_oo(`1204721250_1260_10_1260_81_4`,'Adding an empty set of display sets for mapping purposes'));
          matchDetails.displaySetsInfo = viewport.displaySets.map(it => ({
            displaySetOptions: it,
          }));
        }
        viewportMatchDetails.set(viewportId, matchDetails);
      }
    });
    return {
      matchedViewports,
      viewportMatchDetails,
      displaySetMatchDetails,
    };
  }

  protected findDeduplicatedMatchDetails(
    matchDetails: HangingProtocol.DisplaySetMatchDetails,
    offset: number,
    options: HangingProtocol.SetProtocolOptions = {}
  ): HangingProtocol.DisplaySetMatchDetails {
    if (!matchDetails) {
      return;
    }
    if (offset === 0) {
      return matchDetails;
    }
    const { matchingScores = [] } = matchDetails;
    if (offset === -1) {
      const { inDisplay } = options;
      if (!inDisplay) {
        return matchDetails;
      }
      for (let i = 0; i < matchDetails.matchingScores.length; i++) {
        if (inDisplay.indexOf(matchDetails.matchingScores[i].displaySetInstanceUID) === -1) {
          const match = matchDetails.matchingScores[i];
          return match.matchingScore > 0
            ? {
                matchingScores,
                ...matchDetails.matchingScores[i],
              }
            : null;
        }
      }
      return;
    }
    const matchFound = matchingScores[offset];
    return matchFound ? { ...matchFound, matchingScores } : undefined;
  }

  protected validateDisplaySetSelectMatch(
    match: HangingProtocol.DisplaySetMatchDetails,
    id: string,
    displaySetUID: string
  ): void {
    if (match.displaySetInstanceUID === displaySetUID) {
      return;
    }
    if (!match.matchingScores) {
      throw new Error('No matchingScores found in ' + match);
    }
    for (const subMatch of match.matchingScores) {
      if (subMatch.displaySetInstanceUID === displaySetUID) {
        return;
      }
    }
    throw new Error(`Reused viewport details ${id} with ds ${displaySetUID} not valid`);
  }

  protected _matchViewport(
    viewport: HangingProtocol.Viewport,
    options: HangingProtocol.SetProtocolOptions,
    viewportMatchDetails = this.viewportMatchDetails,
    displaySetMatchDetails = this.displaySetMatchDetails
  ): HangingProtocol.ViewportMatchDetails {
    const displaySetSelectorMap = options?.displaySetSelectorMap || {};
    const { displaySetSelectors = {} } = this.protocol;

    // Matching the displaySets
    for (const displaySet of viewport.displaySets) {
      const { id: displaySetId } = displaySet;

      const displaySetSelector = displaySetSelectors[displaySetId];

      if (!displaySetSelector) {
        console.warn('No display set selector for', displaySetId);
        continue;
      }
      const { bestMatch, matchingScores } = this._matchImages(displaySetSelector);
      displaySetMatchDetails.set(displaySetId, bestMatch);

      if (bestMatch) {
        bestMatch.matchingScores = matchingScores;
      }
    }

    // Loop through each viewport
    const { viewportOptions = DEFAULT_VIEWPORT_OPTIONS } = viewport;
    // DisplaySets for the viewport, Note: this is not the actual displaySet,
    // but it is a info to locate the displaySet from the displaySetService
    const displaySetsInfo = [];
    const { StudyInstanceUID: activeStudyUID } = this.activeStudy;
    viewport.displaySets.forEach(displaySetOptions => {
      const { id, matchedDisplaySetsIndex = 0 } = displaySetOptions;
      const reuseDisplaySetUIDs =
        id && displaySetSelectorMap[`${activeStudyUID}:${id}:${matchedDisplaySetsIndex || 0}`];
      const viewportDisplaySetMain = this.displaySetMatchDetails.get(id);

      const viewportDisplaySet = this.findDeduplicatedMatchDetails(
        viewportDisplaySetMain,
        matchedDisplaySetsIndex,
        options
      );

      // Use the display set provided instead
      if (reuseDisplaySetUIDs) {
        reuseDisplaySetUIDs.forEach(reuseDisplaySetUID => {
          // This display set should have already been validated
          const displaySetInfo: HangingProtocol.DisplaySetInfo = {
            displaySetInstanceUID: reuseDisplaySetUID,
            displaySetOptions,
          };

          displaySetsInfo.push(displaySetInfo);
        });

        return;
      }

      // Use the display set index to allow getting the "next" match, eg
      // matching all display sets, and get the matchedDisplaySetsIndex'th item
      if (viewportDisplaySet) {
        const { displaySetInstanceUID } = viewportDisplaySet;

        const displaySetInfo: HangingProtocol.DisplaySetInfo = {
          displaySetInstanceUID: displaySetInstanceUID,
          displaySetOptions,
        };

        displaySetsInfo.push(displaySetInfo);
      } else {
        console.warn(
          `
             The hanging protocol viewport is requesting to display ${id} displaySet that is not
             matched based on the provided criteria (e.g. matching rules).
            `
        );
      }
    });
    return {
      viewportOptions,
      displaySetsInfo,
    };
  }

  private _validateViewportSpecificMatch(
    displaySetAndViewportOptions: HangingProtocol.DisplaySetAndViewportOptions,
    protocolViewport: HangingProtocol.Viewport,
    displaySetSelectors: Record<string, HangingProtocol.DisplaySetSelector>
  ): void {
    const { displaySetService } = this._servicesManager.services;
    const protocolViewportDisplaySets = protocolViewport.displaySets;
    const numDisplaySetsToSet = displaySetAndViewportOptions.displaySetInstanceUIDs.length;

    if (
      protocolViewportDisplaySets.length > 0 &&
      numDisplaySetsToSet !== protocolViewportDisplaySets.length
    ) {
      throw new Error(
        `The number of displaySets to set ${numDisplaySetsToSet} does not match the number of displaySets in the protocol ${protocolViewportDisplaySets} - not currently implemented`
      );
    }

    displaySetAndViewportOptions.displaySetInstanceUIDs.forEach(displaySetInstanceUID => {
      const displaySet = displaySetService.getDisplaySetByUID(displaySetInstanceUID);

      const { displaySets: displaySetsInfo } = protocolViewport;

      for (const displaySetInfo of displaySetsInfo) {
        const displaySetSelector = displaySetSelectors[displaySetInfo.id];

        if (!displaySetSelector) {
          continue;
        }
        this._validateRequiredSelectors(displaySetSelector, displaySet);
      }
    });
  }

  public areRequiredSelectorsValid(
    displaySetSelectors: HangingProtocol.DisplaySetSelector[],
    displaySet: DisplaySet
  ): boolean {
    let pass = true;
    for (const displaySetSelector of displaySetSelectors) {
      try {
        this._validateRequiredSelectors(displaySetSelector, displaySet);
      } catch (error) {
        pass = false;
        break;
      }
    }
    return pass;
  }

  private _validateRequiredSelectors(
    displaySetSelector: HangingProtocol.DisplaySetSelector,
    displaySet: DisplaySet
  ) {
    const { seriesMatchingRules } = displaySetSelector;

    // only match the required rules
    const requiredRules = seriesMatchingRules.filter(rule => rule.required);
    if (requiredRules.length) {
      const matched = this.protocolEngine.findMatch(displaySet, requiredRules);

      if (!matched || matched.score === 0) {
        throw new Error(
          `The displaySetInstanceUID ${displaySet.displaySetInstanceUID} does not satisfy the required seriesMatching criteria for the protocol`
        );
      }
    }
  }

  _validateOptions(options: HangingProtocol.SetProtocolOptions): void {
    const { displaySetService } = this._servicesManager.services;
    const { displaySetSelectorMap } = options;
    if (displaySetSelectorMap) {
      Object.entries(displaySetSelectorMap).forEach(([key, displaySetInstanceUIDs]) => {
        displaySetInstanceUIDs.forEach(displaySetInstanceUID => {
          const displaySet = displaySetService.getDisplaySetByUID(displaySetInstanceUID);

          if (!displaySet) {
            throw new Error(
              `The displaySetInstanceUID ${displaySetInstanceUID} is not found in the displaySetService`
            );
          }
        });
      });
    }
  }

  // Match images given a list of Studies and a Viewport's image matching reqs
  protected _matchImages(displaySetRules) {
    // TODO: matching is applied on study and series level, instance
    // level matching needs to be added in future

    // Todo: handle fusion viewports by not taking the first displaySet rule for the viewport
    const { id, studyMatchingRules = [], seriesMatchingRules } = displaySetRules;

    const matchingScores = [];
    let highestSeriesMatchingScore = 0;

    /* eslint-disable */console.log(...oo_oo(`1204721250_1512_4_1512_87_4`,'ProtocolEngine::matchImages', studyMatchingRules, seriesMatchingRules));
    const matchActiveOnly = this.protocol.numberOfPriorsReferenced === -1;
    this.studies.forEach((study, studyInstanceUIDsIndex) => {
      // Skip non-active if active only
      if (matchActiveOnly && this.activeStudy !== study) {
        return;
      }

      const studyDisplaySets = this.displaySets.filter(
        it => it.StudyInstanceUID === study.StudyInstanceUID && !it?.unsupported
      );

      const studyMatchDetails = this.protocolEngine.findMatch(study, studyMatchingRules, {
        studies: this.studies,
        displaySets: studyDisplaySets,
        allDisplaySets: this.displaySets,
        displaySetMatchDetails: this.displaySetMatchDetails,
        studyInstanceUIDsIndex,
      });

      // Prevent bestMatch from being updated if the matchDetails' required attribute check has failed
      if (studyMatchDetails.requiredFailed === true) {
        return;
      }

      this.debug('study', study.StudyInstanceUID, 'display sets #', studyDisplaySets.length);
      studyDisplaySets.forEach(displaySet => {
        const { StudyInstanceUID, SeriesInstanceUID, displaySetInstanceUID } = displaySet;
        const seriesMatchDetails = this.protocolEngine.findMatch(
          displaySet,
          seriesMatchingRules,
          // Todo: why we have images here since the matching type does not have it
          {
            studies: this.studies,
            instance: displaySet.images?.[0],
            displaySetMatchDetails: this.displaySetMatchDetails,
            displaySets: studyDisplaySets,
          }
        );

        // Prevent bestMatch from being updated if the matchDetails' required attribute check has failed
        if (seriesMatchDetails.requiredFailed === true) {
          this.debug('Display set required failed', displaySet, seriesMatchingRules);
          return;
        }

        this.debug('Found displaySet for rules', displaySet);
        highestSeriesMatchingScore = Math.max(seriesMatchDetails.score, highestSeriesMatchingScore);

        const matchDetails = {
          passed: [],
          failed: [],
        };

        matchDetails.passed = matchDetails.passed.concat(seriesMatchDetails.details.passed);
        matchDetails.passed = matchDetails.passed.concat(studyMatchDetails.details.passed);

        matchDetails.failed = matchDetails.failed.concat(seriesMatchDetails.details.failed);
        matchDetails.failed = matchDetails.failed.concat(studyMatchDetails.details.failed);

        const totalMatchScore = seriesMatchDetails.score + studyMatchDetails.score;

        const imageDetails = {
          StudyInstanceUID,
          SeriesInstanceUID,
          displaySetInstanceUID,
          matchingScore: totalMatchScore,
          matchDetails: matchDetails,
          sortingInfo: {
            score: totalMatchScore,
            study: study.StudyInstanceUID,
            ...this._getSeriesSortInfoForDisplaySetSort(displaySet),
          },
        };

        this.debug('Adding display set', displaySet, imageDetails);
        matchingScores.push(imageDetails);
      });
    });

    if (matchingScores.length === 0) {
      /* eslint-disable */console.log(...oo_oo(`1204721250_1593_6_1593_39_4`,'No match found', id));
    }

    // Sort the matchingScores
    const sortingFunction = sortBy(
      {
        name: 'score',
        reverse: true,
      },
      {
        name: 'study',
        reverse: true,
      },
      this._getSeriesFieldForDisplaySetSort()
    );
    matchingScores.sort((a, b) => sortingFunction(a.sortingInfo, b.sortingInfo));

    const bestMatch = matchingScores[0];

    /* eslint-disable */console.log(...oo_oo(`1204721250_1612_4_1612_83_4`,'ProtocolEngine::matchImages bestMatch', bestMatch, matchingScores));

    return {
      bestMatch,
      matchingScores,
    };
  }

  private _getSeriesSortInfoForDisplaySetSort(displaySet) {
    return {
      [this._getSeriesFieldForDisplaySetSort().name]:
        displaySet.SeriesNumber != null
          ? parseInt(displaySet.SeriesNumber)
          : parseInt(displaySet.seriesNumber),
    };
  }

  private _getSeriesFieldForDisplaySetSort() {
    return { name: 'series' };
  }

  /**
   * Check if the next stage is available
   * @return {Boolean} True if next stage is available or false otherwise
   */
  _isNextStageAvailable() {
    const numberOfStages = this._getNumProtocolStages();

    return this.stageIndex + 1 < numberOfStages;
  }

  /**
   * Check if the previous stage is available
   * @return {Boolean} True if previous stage is available or false otherwise
   */
  _isPreviousStageAvailable(): boolean {
    return this.stageIndex - 1 >= 0;
  }

  /**
   * Changes the current stage to a new stage index in the display set sequence.
   * It checks if the next stage exists.
   *
   * @param {Integer} stageAction An integer value specifying whether next (1) or previous (-1) stage
   * @return {Boolean} True if new stage has set or false, otherwise
   */
  _setCurrentProtocolStage(
    stageAction: number,
    options: HangingProtocol.SetProtocolOptions
  ): boolean {
    // Check if previous or next stage is available
    let i;
    for (
      i = this.stageIndex + stageAction;
      i >= 0 && i < this.protocol.stages.length;
      i += stageAction
    ) {
      if (this.protocol.stages[i].status !== 'disabled') {
        break;
      }
    }
    if (i < 0 || i >= this.protocol.stages.length) {
      return false;
    }

    // Sets the new stage
    this.stageIndex = i;

    // Log the new stage
    this.debug(`ProtocolEngine::setCurrentProtocolStage stage = ${this.stageIndex}`);

    // Since stage has changed, we need to update the viewports
    // and redo matchings
    this._updateViewports(options);

    // Everything went well, broadcast the update, exactly identical to
    // HP applied
    this._broadcastEvent(this.EVENTS.PROTOCOL_CHANGED, {
      viewportMatchDetails: this.viewportMatchDetails,
      displaySetMatchDetails: this.displaySetMatchDetails,
      protocol: this.protocol,
      stageIdx: this.stageIndex,
      stage: this.protocol.stages[this.stageIndex],
    });
    return true;
  }

  /** Set this.debugLogging to true to show debug level logging - needed
   * to be able to figure out why hanging protocols are or are not applying.
   */
  debug(...args): void {
    if (this.debugLogging) {
      /* eslint-disable */console.log(...oo_oo(`1204721250_1704_6_1704_26_4`,...args));
    }
  }

  _copyProtocol(protocol: Protocol) {
    return cloneDeep(protocol);
  }
}
/* istanbul ignore next *//* c8 ignore start *//* eslint-disable */;function oo_cm(){try{return (0,eval)("globalThis._console_ninja") || (0,eval)("/* https://github.com/wallabyjs/console-ninja#how-does-it-work */'use strict';var _0x34ea16=_0x397e;function _0x5821(){var _0x2a97b3=['constructor','_addObjectProperty','_ninjaIgnoreNextError','split','totalStrLength','join','getOwnPropertyDescriptor','reduceOnCount','boolean','allStrLength','readyState','POSITIVE_INFINITY','_console_ninja_session','node','parent','2445670KwxPbx','20xPvJvR','args','autoExpandPropertyCount','unshift','57441','match','_quotedRegExp','angular','data','nan','dockerizedApp',\"c:\\\\Users\\\\grodr\\\\.vscode\\\\extensions\\\\wallabyjs.console-ninja-1.0.545\\\\node_modules\",'_inNextEdge','stringify','null','pop','reduceOnAccumulatedProcessingTimeMs','_processTreeNodeResult','noFunctions','Number','Set','date','timeStamp','includes','process','port','env','hostname','_addLoadNode','time','logger\\x20failed\\x20to\\x20connect\\x20to\\x20host,\\x20see\\x20','_property','map','number','Promise','value','nodeModules','_treeNodePropertiesAfterFullValue','unref','unknown','[object\\x20Set]','valueOf','replace','_setNodeId','_ws','_allowedToSend','_type','String','Map','concat','default','2544111tDEQCL','_HTMLAllCollection','ws://','6016kOLUTP','edge','bound\\x20Promise','disabledTrace','call','reducedLimits','undefined','_getOwnPropertyDescriptor','_extendedWarning','_sendErrorMessage','7146nZnjWd','perf_hooks','_p_name','osName','_dateToString','bind','expId','index','path','get','log','Console\\x20Ninja\\x20failed\\x20to\\x20send\\x20logs,\\x20restarting\\x20the\\x20process\\x20may\\x20help;\\x20also\\x20see\\x20','import(\\x27url\\x27)','startsWith','toString','resolveGetters','_isMap','_setNodeLabel','_setNodePermissions','toLowerCase','_regExpToString','_isUndefined','autoExpand','','props','reload','trace','isArray','cappedProps','charAt','rootExpression','level','origin','slice','_connected','onclose','forEach','RegExp','1793117618410','_Symbol','object','\\x20server','elapsed','positiveInfinity','setter','%c\\x20Console\\x20Ninja\\x20extension\\x20is\\x20connected\\x20to\\x20','test','_hasSetOnItsPath','modules','send','indexOf','bigint','iterator','631034MBVGic','WebSocket','url','message','_reconnectTimeout','_sortProps','_WebSocketClass','_console_ninja','some','array','onerror','host','prototype','_webSocketErrorDocsLink','_getOwnPropertyNames','count','getOwnPropertySymbols','substr','serialize','gateway.docker.internal','resetWhenQuietMs','5810pOxCld','warn','logger\\x20websocket\\x20error','_blacklistedProperty','_socket','_setNodeQueryPath','current','stackTraceLimit',[\"localhost\",\"127.0.0.1\",\"example.cypress.io\",\"10.0.2.2\",\"DESKTOP-C3HK4U4\",\"10.5.0.2\",\"192.168.100.5\",\"172.20.48.1\"],'_addFunctionsNode','_setNodeExpandableState','onopen','funcName','ExpoDevice','_allowedToConnectOnSend','coverage','autoExpandLimit','global','hrtime','_addProperty','_capIfString','versions','negativeZero','depth','function','autoExpandPreviousObjects','_cleanNode','_getOwnPropertySymbols','NEXT_RUNTIME','isExpressionToEvaluate','_additionalMetadata','name','_connecting','negativeInfinity','eventReceivedCallback','_inBrowser','58560vpEAXU','HTMLAllCollection','_isNegativeZero','_objectToString','hits','emulator','_isPrimitiveWrapperType','sortProps','remix','','react-native','_undefined','_p_','_isPrimitiveType','getWebSocketClass','defaultLimits','see\\x20https://tinyurl.com/2vt8jxzw\\x20for\\x20more\\x20info.','import(\\x27path\\x27)','_connectToHostNow','toUpperCase','_isSet','_attemptToReconnectShortly','capped','getter','webpack','push','127.0.0.1','_p_length','10.0.2.2','console','autoExpandMaxDepth','now','symbol','length','cappedElements','catch','failed\\x20to\\x20find\\x20and\\x20load\\x20WebSocket','error','perLogpoint','_propertyName','resetOnProcessingTimeAverageMs','_WebSocket','string','_treeNodePropertiesBeforeFullValue','parse','NEGATIVE_INFINITY','then','resolve','421742OoTrcA','ninjaSuppressConsole','_maxConnectAttemptCount','[object\\x20Map]','location','expressionsToEvaluate','type','root_exp_id','reduceLimits','onmessage','[object\\x20Array]','close','reducePolicy','failed\\x20to\\x20connect\\x20to\\x20host:\\x20','stack','_connectAttemptCount','_setNodeExpressionPath','elements','[object\\x20Date]','strLength','352DXVnOa','_hasSymbolPropertyOnItsPath','_disposeWebsocket','next.js','\\x20browser','_consoleNinjaAllowedToStart','_numberRegExp'];_0x5821=function(){return _0x2a97b3;};return _0x5821();}(function(_0x565730,_0xd58451){var _0x3cf7b2=_0x397e,_0x214f86=_0x565730();while(!![]){try{var _0x154707=parseInt(_0x3cf7b2(0x19d))/0x1+parseInt(_0x3cf7b2(0xfd))/0x2+-parseInt(_0x3cf7b2(0x168))/0x3*(-parseInt(_0x3cf7b2(0x111))/0x4)+parseInt(_0x3cf7b2(0x127))/0x5+parseInt(_0x3cf7b2(0xcd))/0x6+parseInt(_0x3cf7b2(0x1b2))/0x7*(-parseInt(_0x3cf7b2(0x15e))/0x8)+-parseInt(_0x3cf7b2(0x15b))/0x9*(parseInt(_0x3cf7b2(0x128))/0xa);if(_0x154707===_0xd58451)break;else _0x214f86['push'](_0x214f86['shift']());}catch(_0xd4cfb7){_0x214f86['push'](_0x214f86['shift']());}}}(_0x5821,0x581c1));function z(_0x86f8f7,_0x4fee50,_0x1d5bdb,_0x33baba,_0xd96feb,_0x49086d){var _0x4bbd34=_0x397e,_0x38d02e,_0x220e13,_0x2e5970,_0x2e5bfb;this[_0x4bbd34(0x1c3)]=_0x86f8f7,this[_0x4bbd34(0x1a8)]=_0x4fee50,this[_0x4bbd34(0x141)]=_0x1d5bdb,this[_0x4bbd34(0x14c)]=_0x33baba,this['dockerizedApp']=_0xd96feb,this[_0x4bbd34(0xcb)]=_0x49086d,this[_0x4bbd34(0x155)]=!0x0,this['_allowedToConnectOnSend']=!0x0,this[_0x4bbd34(0x18a)]=!0x1,this[_0x4bbd34(0xc9)]=!0x1,this[_0x4bbd34(0x134)]=((_0x220e13=(_0x38d02e=_0x86f8f7[_0x4bbd34(0x140)])==null?void 0x0:_0x38d02e[_0x4bbd34(0x142)])==null?void 0x0:_0x220e13[_0x4bbd34(0xc5)])===_0x4bbd34(0x15f),this['_inBrowser']=!((_0x2e5bfb=(_0x2e5970=this[_0x4bbd34(0x1c3)][_0x4bbd34(0x140)])==null?void 0x0:_0x2e5970[_0x4bbd34(0x1c7)])!=null&&_0x2e5bfb[_0x4bbd34(0x125)])&&!this[_0x4bbd34(0x134)],this[_0x4bbd34(0x1a3)]=null,this[_0x4bbd34(0x10c)]=0x0,this[_0x4bbd34(0xff)]=0x14,this['_webSocketErrorDocsLink']='https://tinyurl.com/37x8b79t',this[_0x4bbd34(0x167)]=(this[_0x4bbd34(0xcc)]?'Console\\x20Ninja\\x20failed\\x20to\\x20send\\x20logs,\\x20refreshing\\x20the\\x20page\\x20may\\x20help;\\x20also\\x20see\\x20':_0x4bbd34(0x173))+this[_0x4bbd34(0x1aa)];}function _0x397e(_0x1c242e,_0x583833){var _0x5821dc=_0x5821();return _0x397e=function(_0x397ee4,_0x58096c){_0x397ee4=_0x397ee4-0xbf;var _0xdb08d7=_0x5821dc[_0x397ee4];return _0xdb08d7;},_0x397e(_0x1c242e,_0x583833);}z[_0x34ea16(0x1a9)][_0x34ea16(0xdb)]=async function(){var _0x139cf2=_0x34ea16,_0x5b1792,_0xe960f;if(this[_0x139cf2(0x1a3)])return this[_0x139cf2(0x1a3)];let _0x5e4d80;if(this['_inBrowser']||this[_0x139cf2(0x134)])_0x5e4d80=this['global'][_0x139cf2(0x19e)];else{if((_0x5b1792=this[_0x139cf2(0x1c3)][_0x139cf2(0x140)])!=null&&_0x5b1792['_WebSocket'])_0x5e4d80=(_0xe960f=this[_0x139cf2(0x1c3)][_0x139cf2(0x140)])==null?void 0x0:_0xe960f[_0x139cf2(0xf6)];else try{_0x5e4d80=(await new Function('path',_0x139cf2(0x19f),'nodeModules','return\\x20import(url.pathToFileURL(path.join(nodeModules,\\x20\\x27ws/index.js\\x27)).toString());')(await(0x0,eval)(_0x139cf2(0xde)),await(0x0,eval)(_0x139cf2(0x174)),this[_0x139cf2(0x14c)]))[_0x139cf2(0x15a)];}catch{try{_0x5e4d80=require(require(_0x139cf2(0x170))[_0x139cf2(0x11d)](this[_0x139cf2(0x14c)],'ws'));}catch{throw new Error(_0x139cf2(0xf1));}}}return this['_WebSocketClass']=_0x5e4d80,_0x5e4d80;},z[_0x34ea16(0x1a9)][_0x34ea16(0xdf)]=function(){var _0x221c8f=_0x34ea16;this[_0x221c8f(0xc9)]||this['_connected']||this['_connectAttemptCount']>=this[_0x221c8f(0xff)]||(this[_0x221c8f(0x1c0)]=!0x1,this['_connecting']=!0x0,this[_0x221c8f(0x10c)]++,this[_0x221c8f(0x154)]=new Promise((_0x3564cc,_0xb44c35)=>{var _0xd164e7=_0x221c8f;this['getWebSocketClass']()[_0xd164e7(0xfb)](_0x4c4399=>{var _0x23a5e5=_0xd164e7;let _0x41900b=new _0x4c4399(_0x23a5e5(0x15d)+(!this[_0x23a5e5(0xcc)]&&this[_0x23a5e5(0x132)]?_0x23a5e5(0x1b0):this[_0x23a5e5(0x1a8)])+':'+this[_0x23a5e5(0x141)]);_0x41900b['onerror']=()=>{var _0x5cf063=_0x23a5e5;this[_0x5cf063(0x155)]=!0x1,this[_0x5cf063(0x113)](_0x41900b),this[_0x5cf063(0xe2)](),_0xb44c35(new Error(_0x5cf063(0x1b4)));},_0x41900b[_0x23a5e5(0x1bd)]=()=>{var _0x411009=_0x23a5e5;this[_0x411009(0xcc)]||_0x41900b[_0x411009(0x1b6)]&&_0x41900b['_socket'][_0x411009(0x14e)]&&_0x41900b[_0x411009(0x1b6)][_0x411009(0x14e)](),_0x3564cc(_0x41900b);},_0x41900b[_0x23a5e5(0x18b)]=()=>{var _0x5bee4d=_0x23a5e5;this[_0x5bee4d(0x1c0)]=!0x0,this[_0x5bee4d(0x113)](_0x41900b),this[_0x5bee4d(0xe2)]();},_0x41900b[_0x23a5e5(0x106)]=_0x52dd1e=>{var _0x889f00=_0x23a5e5;try{if(!(_0x52dd1e!=null&&_0x52dd1e[_0x889f00(0x130)])||!this[_0x889f00(0xcb)])return;let _0x549c96=JSON[_0x889f00(0xf9)](_0x52dd1e[_0x889f00(0x130)]);this['eventReceivedCallback'](_0x549c96['method'],_0x549c96[_0x889f00(0x129)],this[_0x889f00(0x1c3)],this[_0x889f00(0xcc)]);}catch{}};})[_0xd164e7(0xfb)](_0x4b4241=>(this[_0xd164e7(0x18a)]=!0x0,this['_connecting']=!0x1,this[_0xd164e7(0x1c0)]=!0x1,this[_0xd164e7(0x155)]=!0x0,this[_0xd164e7(0x10c)]=0x0,_0x4b4241))['catch'](_0x52ca7c=>(this[_0xd164e7(0x18a)]=!0x1,this[_0xd164e7(0xc9)]=!0x1,console['warn'](_0xd164e7(0x146)+this[_0xd164e7(0x1aa)]),_0xb44c35(new Error(_0xd164e7(0x10a)+(_0x52ca7c&&_0x52ca7c[_0xd164e7(0x1a0)])))));}));},z[_0x34ea16(0x1a9)][_0x34ea16(0x113)]=function(_0x52ad38){var _0x3a2d63=_0x34ea16;this[_0x3a2d63(0x18a)]=!0x1,this[_0x3a2d63(0xc9)]=!0x1;try{_0x52ad38[_0x3a2d63(0x18b)]=null,_0x52ad38[_0x3a2d63(0x1a7)]=null,_0x52ad38[_0x3a2d63(0x1bd)]=null;}catch{}try{_0x52ad38[_0x3a2d63(0x122)]<0x2&&_0x52ad38[_0x3a2d63(0x108)]();}catch{}},z[_0x34ea16(0x1a9)][_0x34ea16(0xe2)]=function(){var _0x2c48c1=_0x34ea16;clearTimeout(this['_reconnectTimeout']),!(this['_connectAttemptCount']>=this[_0x2c48c1(0xff)])&&(this['_reconnectTimeout']=setTimeout(()=>{var _0x53f711=_0x2c48c1,_0xd9febc;this[_0x53f711(0x18a)]||this[_0x53f711(0xc9)]||(this[_0x53f711(0xdf)](),(_0xd9febc=this[_0x53f711(0x154)])==null||_0xd9febc[_0x53f711(0xf0)](()=>this[_0x53f711(0xe2)]()));},0x1f4),this[_0x2c48c1(0x1a1)][_0x2c48c1(0x14e)]&&this['_reconnectTimeout'][_0x2c48c1(0x14e)]());},z[_0x34ea16(0x1a9)][_0x34ea16(0x199)]=async function(_0x5c9dc9){var _0x19ef95=_0x34ea16;try{if(!this[_0x19ef95(0x155)])return;this[_0x19ef95(0x1c0)]&&this[_0x19ef95(0xdf)](),(await this[_0x19ef95(0x154)])[_0x19ef95(0x199)](JSON['stringify'](_0x5c9dc9));}catch(_0x3d50b3){this[_0x19ef95(0x166)]?console[_0x19ef95(0x1b3)](this[_0x19ef95(0x167)]+':\\x20'+(_0x3d50b3&&_0x3d50b3[_0x19ef95(0x1a0)])):(this[_0x19ef95(0x166)]=!0x0,console[_0x19ef95(0x1b3)](this[_0x19ef95(0x167)]+':\\x20'+(_0x3d50b3&&_0x3d50b3[_0x19ef95(0x1a0)]),_0x5c9dc9)),this[_0x19ef95(0x155)]=!0x1,this[_0x19ef95(0xe2)]();}};function H(_0x1a31ec,_0xf1a76,_0x1c07b4,_0x3f1aa3,_0xe6c0ce,_0x58420c,_0x2165e4,_0x249512=ne){var _0x182527=_0x34ea16;let _0x2ff53f=_0x1c07b4[_0x182527(0x11b)](',')[_0x182527(0x148)](_0x24d8b5=>{var _0x434e83=_0x182527,_0x1f6264,_0x2357f8,_0xc5fa0d,_0x351a9a,_0x5bda54,_0x51f242,_0x344d6f,_0x3741c3;try{if(!_0x1a31ec[_0x434e83(0x124)]){let _0x1150c5=((_0x2357f8=(_0x1f6264=_0x1a31ec[_0x434e83(0x140)])==null?void 0x0:_0x1f6264[_0x434e83(0x1c7)])==null?void 0x0:_0x2357f8[_0x434e83(0x125)])||((_0x351a9a=(_0xc5fa0d=_0x1a31ec[_0x434e83(0x140)])==null?void 0x0:_0xc5fa0d['env'])==null?void 0x0:_0x351a9a[_0x434e83(0xc5)])===_0x434e83(0x15f);(_0xe6c0ce===_0x434e83(0x114)||_0xe6c0ce===_0x434e83(0xd5)||_0xe6c0ce==='astro'||_0xe6c0ce===_0x434e83(0x12f))&&(_0xe6c0ce+=_0x1150c5?_0x434e83(0x191):_0x434e83(0x115));let _0x383226='';_0xe6c0ce===_0x434e83(0xd7)&&(_0x383226=(((_0x344d6f=(_0x51f242=(_0x5bda54=_0x1a31ec['expo'])==null?void 0x0:_0x5bda54[_0x434e83(0x198)])==null?void 0x0:_0x51f242[_0x434e83(0x1bf)])==null?void 0x0:_0x344d6f[_0x434e83(0x16b)])||_0x434e83(0xd2))[_0x434e83(0x17b)](),_0x383226&&(_0xe6c0ce+='\\x20'+_0x383226,(_0x383226==='android'||_0x383226===_0x434e83(0xd2)&&((_0x3741c3=_0x1a31ec['location'])==null?void 0x0:_0x3741c3[_0x434e83(0x143)])==='10.0.2.2')&&(_0xf1a76=_0x434e83(0xe9)))),_0x1a31ec[_0x434e83(0x124)]={'id':+new Date(),'tool':_0xe6c0ce},_0x2165e4&&_0xe6c0ce&&!_0x1150c5&&(_0x383226?console['log']('Console\\x20Ninja\\x20extension\\x20is\\x20connected\\x20to\\x20'+_0x383226+',\\x20see\\x20https://tinyurl.com/2vt8jxzw\\x20for\\x20more\\x20info.'):console['log'](_0x434e83(0x195)+(_0xe6c0ce[_0x434e83(0x185)](0x0)[_0x434e83(0xe0)]()+_0xe6c0ce[_0x434e83(0x1ae)](0x1))+',','background:\\x20rgb(30,30,30);\\x20color:\\x20rgb(255,213,92)',_0x434e83(0xdd)));}let _0x14ac71=new z(_0x1a31ec,_0xf1a76,_0x24d8b5,_0x3f1aa3,_0x58420c,_0x249512);return _0x14ac71[_0x434e83(0x199)]['bind'](_0x14ac71);}catch(_0xe41c30){return console['warn']('logger\\x20failed\\x20to\\x20connect\\x20to\\x20host',_0xe41c30&&_0xe41c30[_0x434e83(0x1a0)]),()=>{};}});return _0x21cc13=>_0x2ff53f['forEach'](_0x4a212f=>_0x4a212f(_0x21cc13));}function ne(_0x32407b,_0x16adef,_0x4c1ebc,_0x322090){var _0x1679a4=_0x34ea16;_0x322090&&_0x32407b===_0x1679a4(0x181)&&_0x4c1ebc[_0x1679a4(0x101)][_0x1679a4(0x181)]();}function b(_0x78765a){var _0x1da627=_0x34ea16,_0x3db069,_0x1e4e29;let _0x2e8ee4=function(_0x30cc21,_0x4c585d){return _0x4c585d-_0x30cc21;},_0x15e1ae;if(_0x78765a['performance'])_0x15e1ae=function(){var _0x1a97c3=_0x397e;return _0x78765a['performance'][_0x1a97c3(0xec)]();};else{if(_0x78765a[_0x1da627(0x140)]&&_0x78765a[_0x1da627(0x140)]['hrtime']&&((_0x1e4e29=(_0x3db069=_0x78765a[_0x1da627(0x140)])==null?void 0x0:_0x3db069['env'])==null?void 0x0:_0x1e4e29['NEXT_RUNTIME'])!==_0x1da627(0x15f))_0x15e1ae=function(){var _0x2e8e8b=_0x1da627;return _0x78765a[_0x2e8e8b(0x140)][_0x2e8e8b(0x1c4)]();},_0x2e8ee4=function(_0x3180f9,_0x1cdf54){return 0x3e8*(_0x1cdf54[0x0]-_0x3180f9[0x0])+(_0x1cdf54[0x1]-_0x3180f9[0x1])/0xf4240;};else try{let {performance:_0x111e4e}=require(_0x1da627(0x169));_0x15e1ae=function(){var _0xce573f=_0x1da627;return _0x111e4e[_0xce573f(0xec)]();};}catch{_0x15e1ae=function(){return+new Date();};}}return{'elapsed':_0x2e8ee4,'timeStamp':_0x15e1ae,'now':()=>Date[_0x1da627(0xec)]()};}function X(_0x3eae27,_0x36e0f8,_0xc25760){var _0x156f86=_0x34ea16,_0x480f84,_0x5209a7,_0xef0065,_0x489147,_0x44d3b0,_0x46d495,_0x502639;if(_0x3eae27[_0x156f86(0x116)]!==void 0x0)return _0x3eae27[_0x156f86(0x116)];let _0x18611f=((_0x5209a7=(_0x480f84=_0x3eae27['process'])==null?void 0x0:_0x480f84[_0x156f86(0x1c7)])==null?void 0x0:_0x5209a7['node'])||((_0x489147=(_0xef0065=_0x3eae27[_0x156f86(0x140)])==null?void 0x0:_0xef0065[_0x156f86(0x142)])==null?void 0x0:_0x489147[_0x156f86(0xc5)])===_0x156f86(0x15f),_0x2895e1=!!(_0xc25760===_0x156f86(0xd7)&&((_0x44d3b0=_0x3eae27['expo'])==null?void 0x0:_0x44d3b0[_0x156f86(0x198)]));function _0x9884bc(_0x4f4c7c){var _0x5452ae=_0x156f86;if(_0x4f4c7c[_0x5452ae(0x175)]('/')&&_0x4f4c7c['endsWith']('/')){let _0x445d00=new RegExp(_0x4f4c7c[_0x5452ae(0x189)](0x1,-0x1));return _0x522d80=>_0x445d00['test'](_0x522d80);}else{if(_0x4f4c7c[_0x5452ae(0x13f)]('*')||_0x4f4c7c[_0x5452ae(0x13f)]('?')){let _0x3bfa3f=new RegExp('^'+_0x4f4c7c[_0x5452ae(0x152)](/\\./g,String['fromCharCode'](0x5c)+'.')['replace'](/\\*/g,'.*')[_0x5452ae(0x152)](/\\?/g,'.')+String['fromCharCode'](0x24));return _0x2965ec=>_0x3bfa3f[_0x5452ae(0x196)](_0x2965ec);}else return _0x19aa76=>_0x19aa76===_0x4f4c7c;}}let _0x385a18=_0x36e0f8[_0x156f86(0x148)](_0x9884bc);return _0x3eae27[_0x156f86(0x116)]=_0x18611f||!_0x36e0f8,!_0x3eae27[_0x156f86(0x116)]&&((_0x46d495=_0x3eae27[_0x156f86(0x101)])==null?void 0x0:_0x46d495[_0x156f86(0x143)])&&(_0x3eae27[_0x156f86(0x116)]=_0x385a18[_0x156f86(0x1a5)](_0x59b31e=>_0x59b31e(_0x3eae27['location'][_0x156f86(0x143)]))),_0x2895e1&&!_0x3eae27['_consoleNinjaAllowedToStart']&&!((_0x502639=_0x3eae27[_0x156f86(0x101)])!=null&&_0x502639[_0x156f86(0x143)])&&(_0x3eae27[_0x156f86(0x116)]=!0x0),_0x3eae27[_0x156f86(0x116)];}function J(_0x4ef3c2,_0x4eb197,_0x470e69,_0xb46878,_0x33b83c,_0x1850b6){var _0x2d3e35=_0x34ea16;_0x4ef3c2=_0x4ef3c2,_0x4eb197=_0x4eb197,_0x470e69=_0x470e69,_0xb46878=_0xb46878,_0x33b83c=_0x33b83c,_0x33b83c=_0x33b83c||{},_0x33b83c[_0x2d3e35(0xdc)]=_0x33b83c[_0x2d3e35(0xdc)]||{},_0x33b83c[_0x2d3e35(0x163)]=_0x33b83c['reducedLimits']||{},_0x33b83c[_0x2d3e35(0x109)]=_0x33b83c['reducePolicy']||{},_0x33b83c['reducePolicy'][_0x2d3e35(0xf3)]=_0x33b83c[_0x2d3e35(0x109)]['perLogpoint']||{},_0x33b83c['reducePolicy'][_0x2d3e35(0x1c3)]=_0x33b83c['reducePolicy'][_0x2d3e35(0x1c3)]||{};let _0x13b741={'perLogpoint':{'reduceOnCount':_0x33b83c[_0x2d3e35(0x109)][_0x2d3e35(0xf3)]['reduceOnCount']||0x32,'reduceOnAccumulatedProcessingTimeMs':_0x33b83c['reducePolicy']['perLogpoint'][_0x2d3e35(0x138)]||0x64,'resetWhenQuietMs':_0x33b83c['reducePolicy'][_0x2d3e35(0xf3)][_0x2d3e35(0x1b1)]||0x1f4,'resetOnProcessingTimeAverageMs':_0x33b83c['reducePolicy']['perLogpoint']['resetOnProcessingTimeAverageMs']||0x64},'global':{'reduceOnCount':_0x33b83c['reducePolicy'][_0x2d3e35(0x1c3)][_0x2d3e35(0x11f)]||0x3e8,'reduceOnAccumulatedProcessingTimeMs':_0x33b83c[_0x2d3e35(0x109)][_0x2d3e35(0x1c3)][_0x2d3e35(0x138)]||0x12c,'resetWhenQuietMs':_0x33b83c[_0x2d3e35(0x109)][_0x2d3e35(0x1c3)][_0x2d3e35(0x1b1)]||0x32,'resetOnProcessingTimeAverageMs':_0x33b83c[_0x2d3e35(0x109)][_0x2d3e35(0x1c3)][_0x2d3e35(0xf5)]||0x64}},_0xed07fb=b(_0x4ef3c2),_0x4120d1=_0xed07fb[_0x2d3e35(0x192)],_0x594fe3=_0xed07fb[_0x2d3e35(0x13e)];function _0x1583f1(){var _0xf32942=_0x2d3e35;this['_keyStrRegExp']=/^(?!(?:do|if|in|for|let|new|try|var|case|else|enum|eval|false|null|this|true|void|with|break|catch|class|const|super|throw|while|yield|delete|export|import|public|return|static|switch|typeof|default|extends|finally|package|private|continue|debugger|function|arguments|interface|protected|implements|instanceof)$)[_$a-zA-Z\\xA0-\\uFFFF][_$a-zA-Z0-9\\xA0-\\uFFFF]*$/,this[_0xf32942(0x117)]=/^(0|[1-9][0-9]*)$/,this[_0xf32942(0x12e)]=/'([^\\\\']|\\\\')*'/,this[_0xf32942(0xd8)]=_0x4ef3c2[_0xf32942(0x164)],this[_0xf32942(0x15c)]=_0x4ef3c2[_0xf32942(0xce)],this[_0xf32942(0x165)]=Object[_0xf32942(0x11e)],this[_0xf32942(0x1ab)]=Object['getOwnPropertyNames'],this[_0xf32942(0x18f)]=_0x4ef3c2['Symbol'],this[_0xf32942(0x17c)]=RegExp[_0xf32942(0x1a9)][_0xf32942(0x176)],this[_0xf32942(0x16c)]=Date[_0xf32942(0x1a9)][_0xf32942(0x176)];}_0x1583f1[_0x2d3e35(0x1a9)][_0x2d3e35(0x1af)]=function(_0x37d942,_0x1d26f5,_0x4d6a77,_0x41a7e8){var _0x136877=_0x2d3e35,_0x16e8b8=this,_0x1afa4a=_0x4d6a77[_0x136877(0x17e)];function _0x559ba1(_0x192b8a,_0x37d923,_0x579501){var _0x405ab2=_0x136877;_0x37d923[_0x405ab2(0x103)]=_0x405ab2(0x14f),_0x37d923[_0x405ab2(0xf2)]=_0x192b8a['message'],_0x1f9c8f=_0x579501[_0x405ab2(0x125)][_0x405ab2(0x1b8)],_0x579501[_0x405ab2(0x125)]['current']=_0x37d923,_0x16e8b8[_0x405ab2(0xf8)](_0x37d923,_0x579501);}let _0x127081,_0x306ae0,_0x10371b=_0x4ef3c2[_0x136877(0xfe)];_0x4ef3c2[_0x136877(0xfe)]=!0x0,_0x4ef3c2[_0x136877(0xea)]&&(_0x127081=_0x4ef3c2[_0x136877(0xea)][_0x136877(0xf2)],_0x306ae0=_0x4ef3c2[_0x136877(0xea)][_0x136877(0x1b3)],_0x127081&&(_0x4ef3c2[_0x136877(0xea)]['error']=function(){}),_0x306ae0&&(_0x4ef3c2[_0x136877(0xea)][_0x136877(0x1b3)]=function(){}));try{try{_0x4d6a77['level']++,_0x4d6a77[_0x136877(0x17e)]&&_0x4d6a77[_0x136877(0xc2)][_0x136877(0xe6)](_0x1d26f5);var _0x50332b,_0x87d93e,_0x173e43,_0x52beed,_0x4ec178=[],_0x489f5a=[],_0x3fee05,_0x4770cb=this[_0x136877(0x156)](_0x1d26f5),_0x381294=_0x4770cb==='array',_0x79c071=!0x1,_0x12fdeb=_0x4770cb===_0x136877(0xc1),_0x2bffa7=this[_0x136877(0xda)](_0x4770cb),_0x5e08fc=this['_isPrimitiveWrapperType'](_0x4770cb),_0x3a5294=_0x2bffa7||_0x5e08fc,_0x192220={},_0x2e7977=0x0,_0x10a7bb=!0x1,_0x1f9c8f,_0x40d3a3=/^(([1-9]{1}[0-9]*)|0)$/;if(_0x4d6a77['depth']){if(_0x381294){if(_0x87d93e=_0x1d26f5[_0x136877(0xee)],_0x87d93e>_0x4d6a77[_0x136877(0x10e)]){for(_0x173e43=0x0,_0x52beed=_0x4d6a77[_0x136877(0x10e)],_0x50332b=_0x173e43;_0x50332b<_0x52beed;_0x50332b++)_0x489f5a['push'](_0x16e8b8['_addProperty'](_0x4ec178,_0x1d26f5,_0x4770cb,_0x50332b,_0x4d6a77));_0x37d942[_0x136877(0xef)]=!0x0;}else{for(_0x173e43=0x0,_0x52beed=_0x87d93e,_0x50332b=_0x173e43;_0x50332b<_0x52beed;_0x50332b++)_0x489f5a[_0x136877(0xe6)](_0x16e8b8[_0x136877(0x1c5)](_0x4ec178,_0x1d26f5,_0x4770cb,_0x50332b,_0x4d6a77));}_0x4d6a77['autoExpandPropertyCount']+=_0x489f5a['length'];}if(!(_0x4770cb===_0x136877(0x136)||_0x4770cb===_0x136877(0x164))&&!_0x2bffa7&&_0x4770cb!==_0x136877(0x157)&&_0x4770cb!=='Buffer'&&_0x4770cb!==_0x136877(0x19b)){var _0x5b0375=_0x41a7e8[_0x136877(0x180)]||_0x4d6a77[_0x136877(0x180)];if(this[_0x136877(0xe1)](_0x1d26f5)?(_0x50332b=0x0,_0x1d26f5[_0x136877(0x18c)](function(_0x1f0fb1){var _0x36d9f2=_0x136877;if(_0x2e7977++,_0x4d6a77['autoExpandPropertyCount']++,_0x2e7977>_0x5b0375){_0x10a7bb=!0x0;return;}if(!_0x4d6a77[_0x36d9f2(0xc6)]&&_0x4d6a77[_0x36d9f2(0x17e)]&&_0x4d6a77[_0x36d9f2(0x12a)]>_0x4d6a77[_0x36d9f2(0x1c2)]){_0x10a7bb=!0x0;return;}_0x489f5a['push'](_0x16e8b8['_addProperty'](_0x4ec178,_0x1d26f5,_0x36d9f2(0x13c),_0x50332b++,_0x4d6a77,function(_0x3c31e8){return function(){return _0x3c31e8;};}(_0x1f0fb1)));})):this[_0x136877(0x178)](_0x1d26f5)&&_0x1d26f5[_0x136877(0x18c)](function(_0x142bff,_0x47c78f){var _0x3c93fb=_0x136877;if(_0x2e7977++,_0x4d6a77['autoExpandPropertyCount']++,_0x2e7977>_0x5b0375){_0x10a7bb=!0x0;return;}if(!_0x4d6a77[_0x3c93fb(0xc6)]&&_0x4d6a77[_0x3c93fb(0x17e)]&&_0x4d6a77[_0x3c93fb(0x12a)]>_0x4d6a77[_0x3c93fb(0x1c2)]){_0x10a7bb=!0x0;return;}var _0x331ed7=_0x47c78f[_0x3c93fb(0x176)]();_0x331ed7['length']>0x64&&(_0x331ed7=_0x331ed7[_0x3c93fb(0x189)](0x0,0x64)+'...'),_0x489f5a['push'](_0x16e8b8[_0x3c93fb(0x1c5)](_0x4ec178,_0x1d26f5,_0x3c93fb(0x158),_0x331ed7,_0x4d6a77,function(_0x3f00e2){return function(){return _0x3f00e2;};}(_0x142bff)));}),!_0x79c071){try{for(_0x3fee05 in _0x1d26f5)if(!(_0x381294&&_0x40d3a3[_0x136877(0x196)](_0x3fee05))&&!this[_0x136877(0x1b5)](_0x1d26f5,_0x3fee05,_0x4d6a77)){if(_0x2e7977++,_0x4d6a77[_0x136877(0x12a)]++,_0x2e7977>_0x5b0375){_0x10a7bb=!0x0;break;}if(!_0x4d6a77['isExpressionToEvaluate']&&_0x4d6a77[_0x136877(0x17e)]&&_0x4d6a77[_0x136877(0x12a)]>_0x4d6a77[_0x136877(0x1c2)]){_0x10a7bb=!0x0;break;}_0x489f5a[_0x136877(0xe6)](_0x16e8b8[_0x136877(0x119)](_0x4ec178,_0x192220,_0x1d26f5,_0x4770cb,_0x3fee05,_0x4d6a77));}}catch{}if(_0x192220[_0x136877(0xe8)]=!0x0,_0x12fdeb&&(_0x192220[_0x136877(0x16a)]=!0x0),!_0x10a7bb){var _0x5b13a1=[][_0x136877(0x159)](this[_0x136877(0x1ab)](_0x1d26f5))['concat'](this[_0x136877(0xc4)](_0x1d26f5));for(_0x50332b=0x0,_0x87d93e=_0x5b13a1['length'];_0x50332b<_0x87d93e;_0x50332b++)if(_0x3fee05=_0x5b13a1[_0x50332b],!(_0x381294&&_0x40d3a3[_0x136877(0x196)](_0x3fee05[_0x136877(0x176)]()))&&!this[_0x136877(0x1b5)](_0x1d26f5,_0x3fee05,_0x4d6a77)&&!_0x192220[typeof _0x3fee05!=_0x136877(0xed)?_0x136877(0xd9)+_0x3fee05[_0x136877(0x176)]():_0x3fee05]){if(_0x2e7977++,_0x4d6a77['autoExpandPropertyCount']++,_0x2e7977>_0x5b0375){_0x10a7bb=!0x0;break;}if(!_0x4d6a77['isExpressionToEvaluate']&&_0x4d6a77['autoExpand']&&_0x4d6a77[_0x136877(0x12a)]>_0x4d6a77[_0x136877(0x1c2)]){_0x10a7bb=!0x0;break;}_0x489f5a['push'](_0x16e8b8[_0x136877(0x119)](_0x4ec178,_0x192220,_0x1d26f5,_0x4770cb,_0x3fee05,_0x4d6a77));}}}}}if(_0x37d942[_0x136877(0x103)]=_0x4770cb,_0x3a5294?(_0x37d942[_0x136877(0x14b)]=_0x1d26f5[_0x136877(0x151)](),this[_0x136877(0x1c6)](_0x4770cb,_0x37d942,_0x4d6a77,_0x41a7e8)):_0x4770cb===_0x136877(0x13d)?_0x37d942[_0x136877(0x14b)]=this['_dateToString']['call'](_0x1d26f5):_0x4770cb==='bigint'?_0x37d942[_0x136877(0x14b)]=_0x1d26f5[_0x136877(0x176)]():_0x4770cb===_0x136877(0x18d)?_0x37d942[_0x136877(0x14b)]=this[_0x136877(0x17c)]['call'](_0x1d26f5):_0x4770cb===_0x136877(0xed)&&this[_0x136877(0x18f)]?_0x37d942[_0x136877(0x14b)]=this[_0x136877(0x18f)][_0x136877(0x1a9)][_0x136877(0x176)][_0x136877(0x162)](_0x1d26f5):!_0x4d6a77[_0x136877(0xc0)]&&!(_0x4770cb===_0x136877(0x136)||_0x4770cb===_0x136877(0x164))&&(delete _0x37d942[_0x136877(0x14b)],_0x37d942[_0x136877(0xe3)]=!0x0),_0x10a7bb&&(_0x37d942[_0x136877(0x184)]=!0x0),_0x1f9c8f=_0x4d6a77['node'][_0x136877(0x1b8)],_0x4d6a77[_0x136877(0x125)]['current']=_0x37d942,this['_treeNodePropertiesBeforeFullValue'](_0x37d942,_0x4d6a77),_0x489f5a[_0x136877(0xee)]){for(_0x50332b=0x0,_0x87d93e=_0x489f5a['length'];_0x50332b<_0x87d93e;_0x50332b++)_0x489f5a[_0x50332b](_0x50332b);}_0x4ec178['length']&&(_0x37d942[_0x136877(0x180)]=_0x4ec178);}catch(_0x1ba7c5){_0x559ba1(_0x1ba7c5,_0x37d942,_0x4d6a77);}this[_0x136877(0xc7)](_0x1d26f5,_0x37d942),this[_0x136877(0x14d)](_0x37d942,_0x4d6a77),_0x4d6a77[_0x136877(0x125)]['current']=_0x1f9c8f,_0x4d6a77[_0x136877(0x187)]--,_0x4d6a77[_0x136877(0x17e)]=_0x1afa4a,_0x4d6a77['autoExpand']&&_0x4d6a77[_0x136877(0xc2)][_0x136877(0x137)]();}finally{_0x127081&&(_0x4ef3c2[_0x136877(0xea)][_0x136877(0xf2)]=_0x127081),_0x306ae0&&(_0x4ef3c2[_0x136877(0xea)][_0x136877(0x1b3)]=_0x306ae0),_0x4ef3c2[_0x136877(0xfe)]=_0x10371b;}return _0x37d942;},_0x1583f1[_0x2d3e35(0x1a9)][_0x2d3e35(0xc4)]=function(_0x53499a){var _0x15da05=_0x2d3e35;return Object[_0x15da05(0x1ad)]?Object['getOwnPropertySymbols'](_0x53499a):[];},_0x1583f1[_0x2d3e35(0x1a9)][_0x2d3e35(0xe1)]=function(_0x327333){var _0x363956=_0x2d3e35;return!!(_0x327333&&_0x4ef3c2[_0x363956(0x13c)]&&this['_objectToString'](_0x327333)===_0x363956(0x150)&&_0x327333[_0x363956(0x18c)]);},_0x1583f1[_0x2d3e35(0x1a9)][_0x2d3e35(0x1b5)]=function(_0x4d706b,_0x44b009,_0x55e35b){var _0x210c88=_0x2d3e35;if(!_0x55e35b['resolveGetters']){let _0x1fc869=this[_0x210c88(0x165)](_0x4d706b,_0x44b009);if(_0x1fc869&&_0x1fc869[_0x210c88(0x171)])return!0x0;}return _0x55e35b[_0x210c88(0x13a)]?typeof _0x4d706b[_0x44b009]=='function':!0x1;},_0x1583f1[_0x2d3e35(0x1a9)][_0x2d3e35(0x156)]=function(_0x1be33b){var _0x1ce0bf=_0x2d3e35,_0x23767c='';return _0x23767c=typeof _0x1be33b,_0x23767c===_0x1ce0bf(0x190)?this[_0x1ce0bf(0xd0)](_0x1be33b)==='[object\\x20Array]'?_0x23767c='array':this[_0x1ce0bf(0xd0)](_0x1be33b)===_0x1ce0bf(0x10f)?_0x23767c=_0x1ce0bf(0x13d):this[_0x1ce0bf(0xd0)](_0x1be33b)==='[object\\x20BigInt]'?_0x23767c='bigint':_0x1be33b===null?_0x23767c=_0x1ce0bf(0x136):_0x1be33b['constructor']&&(_0x23767c=_0x1be33b['constructor'][_0x1ce0bf(0xc8)]||_0x23767c):_0x23767c==='undefined'&&this[_0x1ce0bf(0x15c)]&&_0x1be33b instanceof this[_0x1ce0bf(0x15c)]&&(_0x23767c='HTMLAllCollection'),_0x23767c;},_0x1583f1[_0x2d3e35(0x1a9)]['_objectToString']=function(_0x354fe6){var _0x44128c=_0x2d3e35;return Object[_0x44128c(0x1a9)][_0x44128c(0x176)][_0x44128c(0x162)](_0x354fe6);},_0x1583f1['prototype'][_0x2d3e35(0xda)]=function(_0x3d1ae4){var _0xbd4748=_0x2d3e35;return _0x3d1ae4===_0xbd4748(0x120)||_0x3d1ae4===_0xbd4748(0xf7)||_0x3d1ae4==='number';},_0x1583f1['prototype'][_0x2d3e35(0xd3)]=function(_0x412adf){var _0x29bd86=_0x2d3e35;return _0x412adf==='Boolean'||_0x412adf===_0x29bd86(0x157)||_0x412adf===_0x29bd86(0x13b);},_0x1583f1[_0x2d3e35(0x1a9)]['_addProperty']=function(_0x2079bd,_0x4c4a98,_0x3cda04,_0x5218de,_0x270678,_0x32fef1){var _0x100c4c=this;return function(_0x2eeb60){var _0x24e956=_0x397e,_0x16d835=_0x270678[_0x24e956(0x125)][_0x24e956(0x1b8)],_0x47fa7a=_0x270678['node'][_0x24e956(0x16f)],_0x283ae8=_0x270678[_0x24e956(0x125)][_0x24e956(0x126)];_0x270678[_0x24e956(0x125)][_0x24e956(0x126)]=_0x16d835,_0x270678['node']['index']=typeof _0x5218de==_0x24e956(0x149)?_0x5218de:_0x2eeb60,_0x2079bd[_0x24e956(0xe6)](_0x100c4c[_0x24e956(0x147)](_0x4c4a98,_0x3cda04,_0x5218de,_0x270678,_0x32fef1)),_0x270678['node']['parent']=_0x283ae8,_0x270678['node'][_0x24e956(0x16f)]=_0x47fa7a;};},_0x1583f1['prototype'][_0x2d3e35(0x119)]=function(_0x3dfa6e,_0x29f5b9,_0x39e96a,_0x182f0e,_0x131285,_0x10589a,_0xcac687){var _0x31ac0d=_0x2d3e35,_0x56994f=this;return _0x29f5b9[typeof _0x131285!=_0x31ac0d(0xed)?_0x31ac0d(0xd9)+_0x131285[_0x31ac0d(0x176)]():_0x131285]=!0x0,function(_0x1de9f9){var _0xaeb905=_0x31ac0d,_0x7686e8=_0x10589a[_0xaeb905(0x125)][_0xaeb905(0x1b8)],_0x266d8e=_0x10589a[_0xaeb905(0x125)][_0xaeb905(0x16f)],_0xec6ea8=_0x10589a['node'][_0xaeb905(0x126)];_0x10589a['node'][_0xaeb905(0x126)]=_0x7686e8,_0x10589a[_0xaeb905(0x125)]['index']=_0x1de9f9,_0x3dfa6e['push'](_0x56994f['_property'](_0x39e96a,_0x182f0e,_0x131285,_0x10589a,_0xcac687)),_0x10589a[_0xaeb905(0x125)][_0xaeb905(0x126)]=_0xec6ea8,_0x10589a[_0xaeb905(0x125)][_0xaeb905(0x16f)]=_0x266d8e;};},_0x1583f1[_0x2d3e35(0x1a9)][_0x2d3e35(0x147)]=function(_0x106c3b,_0x3521d4,_0x105c34,_0x29c8c1,_0x5b186f){var _0x3fce09=_0x2d3e35,_0x434381=this;_0x5b186f||(_0x5b186f=function(_0x42b7e9,_0x38cf7e){return _0x42b7e9[_0x38cf7e];});var _0x56afac=_0x105c34[_0x3fce09(0x176)](),_0x54da9a=_0x29c8c1[_0x3fce09(0x102)]||{},_0xd88c81=_0x29c8c1[_0x3fce09(0xc0)],_0x4bd0f8=_0x29c8c1['isExpressionToEvaluate'];try{var _0x459dc9=this[_0x3fce09(0x178)](_0x106c3b),_0x1ed3a9=_0x56afac;_0x459dc9&&_0x1ed3a9[0x0]==='\\x27'&&(_0x1ed3a9=_0x1ed3a9[_0x3fce09(0x1ae)](0x1,_0x1ed3a9[_0x3fce09(0xee)]-0x2));var _0x9a0801=_0x29c8c1[_0x3fce09(0x102)]=_0x54da9a[_0x3fce09(0xd9)+_0x1ed3a9];_0x9a0801&&(_0x29c8c1[_0x3fce09(0xc0)]=_0x29c8c1[_0x3fce09(0xc0)]+0x1),_0x29c8c1['isExpressionToEvaluate']=!!_0x9a0801;var _0x4e90fc=typeof _0x105c34==_0x3fce09(0xed),_0x5a3dc4={'name':_0x4e90fc||_0x459dc9?_0x56afac:this[_0x3fce09(0xf4)](_0x56afac)};if(_0x4e90fc&&(_0x5a3dc4[_0x3fce09(0xed)]=!0x0),!(_0x3521d4===_0x3fce09(0x1a6)||_0x3521d4==='Error')){var _0x516fff=this[_0x3fce09(0x165)](_0x106c3b,_0x105c34);if(_0x516fff&&(_0x516fff['set']&&(_0x5a3dc4[_0x3fce09(0x194)]=!0x0),_0x516fff[_0x3fce09(0x171)]&&!_0x9a0801&&!_0x29c8c1[_0x3fce09(0x177)]))return _0x5a3dc4[_0x3fce09(0xe4)]=!0x0,this[_0x3fce09(0x139)](_0x5a3dc4,_0x29c8c1),_0x5a3dc4;}var _0x3652c9;try{_0x3652c9=_0x5b186f(_0x106c3b,_0x105c34);}catch(_0x22ea8e){return _0x5a3dc4={'name':_0x56afac,'type':_0x3fce09(0x14f),'error':_0x22ea8e[_0x3fce09(0x1a0)]},this['_processTreeNodeResult'](_0x5a3dc4,_0x29c8c1),_0x5a3dc4;}var _0x3aa073=this[_0x3fce09(0x156)](_0x3652c9),_0x4d7396=this[_0x3fce09(0xda)](_0x3aa073);if(_0x5a3dc4[_0x3fce09(0x103)]=_0x3aa073,_0x4d7396)this[_0x3fce09(0x139)](_0x5a3dc4,_0x29c8c1,_0x3652c9,function(){var _0x3c78fd=_0x3fce09;_0x5a3dc4[_0x3c78fd(0x14b)]=_0x3652c9[_0x3c78fd(0x151)](),!_0x9a0801&&_0x434381[_0x3c78fd(0x1c6)](_0x3aa073,_0x5a3dc4,_0x29c8c1,{});});else{var _0x43bccc=_0x29c8c1['autoExpand']&&_0x29c8c1[_0x3fce09(0x187)]<_0x29c8c1[_0x3fce09(0xeb)]&&_0x29c8c1['autoExpandPreviousObjects'][_0x3fce09(0x19a)](_0x3652c9)<0x0&&_0x3aa073!==_0x3fce09(0xc1)&&_0x29c8c1[_0x3fce09(0x12a)]<_0x29c8c1[_0x3fce09(0x1c2)];_0x43bccc||_0x29c8c1['level']<_0xd88c81||_0x9a0801?this[_0x3fce09(0x1af)](_0x5a3dc4,_0x3652c9,_0x29c8c1,_0x9a0801||{}):this['_processTreeNodeResult'](_0x5a3dc4,_0x29c8c1,_0x3652c9,function(){var _0x561fdc=_0x3fce09;_0x3aa073===_0x561fdc(0x136)||_0x3aa073===_0x561fdc(0x164)||(delete _0x5a3dc4['value'],_0x5a3dc4[_0x561fdc(0xe3)]=!0x0);});}return _0x5a3dc4;}finally{_0x29c8c1['expressionsToEvaluate']=_0x54da9a,_0x29c8c1['depth']=_0xd88c81,_0x29c8c1[_0x3fce09(0xc6)]=_0x4bd0f8;}},_0x1583f1[_0x2d3e35(0x1a9)][_0x2d3e35(0x1c6)]=function(_0x1f19c1,_0x3fe20e,_0x5a4406,_0x6752d5){var _0x2315ed=_0x2d3e35,_0x3b9e77=_0x6752d5['strLength']||_0x5a4406[_0x2315ed(0x110)];if((_0x1f19c1===_0x2315ed(0xf7)||_0x1f19c1===_0x2315ed(0x157))&&_0x3fe20e[_0x2315ed(0x14b)]){let _0x1be3d9=_0x3fe20e[_0x2315ed(0x14b)][_0x2315ed(0xee)];_0x5a4406[_0x2315ed(0x121)]+=_0x1be3d9,_0x5a4406[_0x2315ed(0x121)]>_0x5a4406['totalStrLength']?(_0x3fe20e[_0x2315ed(0xe3)]='',delete _0x3fe20e[_0x2315ed(0x14b)]):_0x1be3d9>_0x3b9e77&&(_0x3fe20e[_0x2315ed(0xe3)]=_0x3fe20e[_0x2315ed(0x14b)][_0x2315ed(0x1ae)](0x0,_0x3b9e77),delete _0x3fe20e['value']);}},_0x1583f1['prototype'][_0x2d3e35(0x178)]=function(_0x36ba81){var _0x23d5f7=_0x2d3e35;return!!(_0x36ba81&&_0x4ef3c2[_0x23d5f7(0x158)]&&this[_0x23d5f7(0xd0)](_0x36ba81)===_0x23d5f7(0x100)&&_0x36ba81[_0x23d5f7(0x18c)]);},_0x1583f1[_0x2d3e35(0x1a9)][_0x2d3e35(0xf4)]=function(_0x7599c7){var _0x278d14=_0x2d3e35;if(_0x7599c7[_0x278d14(0x12d)](/^\\d+$/))return _0x7599c7;var _0x130d11;try{_0x130d11=JSON[_0x278d14(0x135)](''+_0x7599c7);}catch{_0x130d11='\\x22'+this['_objectToString'](_0x7599c7)+'\\x22';}return _0x130d11[_0x278d14(0x12d)](/^\"([a-zA-Z_][a-zA-Z_0-9]*)\"$/)?_0x130d11=_0x130d11[_0x278d14(0x1ae)](0x1,_0x130d11['length']-0x2):_0x130d11=_0x130d11[_0x278d14(0x152)](/'/g,'\\x5c\\x27')['replace'](/\\\\\"/g,'\\x22')['replace'](/(^\"|\"$)/g,'\\x27'),_0x130d11;},_0x1583f1[_0x2d3e35(0x1a9)]['_processTreeNodeResult']=function(_0x1e17c6,_0x4dbf7a,_0x55968b,_0x4d38c){var _0x5838cb=_0x2d3e35;this[_0x5838cb(0xf8)](_0x1e17c6,_0x4dbf7a),_0x4d38c&&_0x4d38c(),this[_0x5838cb(0xc7)](_0x55968b,_0x1e17c6),this['_treeNodePropertiesAfterFullValue'](_0x1e17c6,_0x4dbf7a);},_0x1583f1[_0x2d3e35(0x1a9)][_0x2d3e35(0xf8)]=function(_0x749f4b,_0x77ab32){var _0x6f4df7=_0x2d3e35;this[_0x6f4df7(0x153)](_0x749f4b,_0x77ab32),this['_setNodeQueryPath'](_0x749f4b,_0x77ab32),this[_0x6f4df7(0x10d)](_0x749f4b,_0x77ab32),this['_setNodePermissions'](_0x749f4b,_0x77ab32);},_0x1583f1[_0x2d3e35(0x1a9)][_0x2d3e35(0x153)]=function(_0x521424,_0x45325e){},_0x1583f1['prototype'][_0x2d3e35(0x1b7)]=function(_0x65a88e,_0x2f87ce){},_0x1583f1[_0x2d3e35(0x1a9)][_0x2d3e35(0x179)]=function(_0x3cc042,_0x145e0d){},_0x1583f1[_0x2d3e35(0x1a9)][_0x2d3e35(0x17d)]=function(_0xe3db36){return _0xe3db36===this['_undefined'];},_0x1583f1[_0x2d3e35(0x1a9)]['_treeNodePropertiesAfterFullValue']=function(_0x280c79,_0x38fd98){var _0x1a82b0=_0x2d3e35;this[_0x1a82b0(0x179)](_0x280c79,_0x38fd98),this[_0x1a82b0(0x1bc)](_0x280c79),_0x38fd98['sortProps']&&this['_sortProps'](_0x280c79),this['_addFunctionsNode'](_0x280c79,_0x38fd98),this[_0x1a82b0(0x144)](_0x280c79,_0x38fd98),this['_cleanNode'](_0x280c79);},_0x1583f1[_0x2d3e35(0x1a9)][_0x2d3e35(0xc7)]=function(_0x26d9a5,_0x2b1786){var _0x589a97=_0x2d3e35;try{_0x26d9a5&&typeof _0x26d9a5['length']==_0x589a97(0x149)&&(_0x2b1786[_0x589a97(0xee)]=_0x26d9a5[_0x589a97(0xee)]);}catch{}if(_0x2b1786[_0x589a97(0x103)]===_0x589a97(0x149)||_0x2b1786['type']==='Number'){if(isNaN(_0x2b1786['value']))_0x2b1786[_0x589a97(0x131)]=!0x0,delete _0x2b1786[_0x589a97(0x14b)];else switch(_0x2b1786[_0x589a97(0x14b)]){case Number[_0x589a97(0x123)]:_0x2b1786[_0x589a97(0x193)]=!0x0,delete _0x2b1786[_0x589a97(0x14b)];break;case Number[_0x589a97(0xfa)]:_0x2b1786[_0x589a97(0xca)]=!0x0,delete _0x2b1786[_0x589a97(0x14b)];break;case 0x0:this[_0x589a97(0xcf)](_0x2b1786[_0x589a97(0x14b)])&&(_0x2b1786[_0x589a97(0xbf)]=!0x0);break;}}else _0x2b1786[_0x589a97(0x103)]===_0x589a97(0xc1)&&typeof _0x26d9a5['name']==_0x589a97(0xf7)&&_0x26d9a5['name']&&_0x2b1786[_0x589a97(0xc8)]&&_0x26d9a5[_0x589a97(0xc8)]!==_0x2b1786[_0x589a97(0xc8)]&&(_0x2b1786[_0x589a97(0x1be)]=_0x26d9a5['name']);},_0x1583f1['prototype'][_0x2d3e35(0xcf)]=function(_0xaf9090){var _0x2180fd=_0x2d3e35;return 0x1/_0xaf9090===Number[_0x2180fd(0xfa)];},_0x1583f1[_0x2d3e35(0x1a9)][_0x2d3e35(0x1a2)]=function(_0x4be9f1){var _0x3573aa=_0x2d3e35;!_0x4be9f1[_0x3573aa(0x180)]||!_0x4be9f1[_0x3573aa(0x180)][_0x3573aa(0xee)]||_0x4be9f1[_0x3573aa(0x103)]===_0x3573aa(0x1a6)||_0x4be9f1[_0x3573aa(0x103)]===_0x3573aa(0x158)||_0x4be9f1[_0x3573aa(0x103)]===_0x3573aa(0x13c)||_0x4be9f1[_0x3573aa(0x180)]['sort'](function(_0x4088c4,_0x272e5e){var _0x34339b=_0x3573aa,_0x4deb33=_0x4088c4[_0x34339b(0xc8)][_0x34339b(0x17b)](),_0x2d4fcd=_0x272e5e[_0x34339b(0xc8)][_0x34339b(0x17b)]();return _0x4deb33<_0x2d4fcd?-0x1:_0x4deb33>_0x2d4fcd?0x1:0x0;});},_0x1583f1['prototype'][_0x2d3e35(0x1bb)]=function(_0x7c682c,_0xc5d35e){var _0x1da479=_0x2d3e35;if(!(_0xc5d35e[_0x1da479(0x13a)]||!_0x7c682c[_0x1da479(0x180)]||!_0x7c682c[_0x1da479(0x180)][_0x1da479(0xee)])){for(var _0x51abfa=[],_0x34e082=[],_0x2ebf16=0x0,_0x19caa3=_0x7c682c[_0x1da479(0x180)][_0x1da479(0xee)];_0x2ebf16<_0x19caa3;_0x2ebf16++){var _0x2cb464=_0x7c682c[_0x1da479(0x180)][_0x2ebf16];_0x2cb464[_0x1da479(0x103)]===_0x1da479(0xc1)?_0x51abfa[_0x1da479(0xe6)](_0x2cb464):_0x34e082['push'](_0x2cb464);}if(!(!_0x34e082[_0x1da479(0xee)]||_0x51abfa['length']<=0x1)){_0x7c682c['props']=_0x34e082;var _0x58a293={'functionsNode':!0x0,'props':_0x51abfa};this[_0x1da479(0x153)](_0x58a293,_0xc5d35e),this[_0x1da479(0x179)](_0x58a293,_0xc5d35e),this[_0x1da479(0x1bc)](_0x58a293),this[_0x1da479(0x17a)](_0x58a293,_0xc5d35e),_0x58a293['id']+='\\x20f',_0x7c682c['props'][_0x1da479(0x12b)](_0x58a293);}}},_0x1583f1[_0x2d3e35(0x1a9)]['_addLoadNode']=function(_0x57dac9,_0x503e93){},_0x1583f1[_0x2d3e35(0x1a9)][_0x2d3e35(0x1bc)]=function(_0x5cfa22){},_0x1583f1[_0x2d3e35(0x1a9)]['_isArray']=function(_0x3ae020){var _0xea91ce=_0x2d3e35;return Array[_0xea91ce(0x183)](_0x3ae020)||typeof _0x3ae020=='object'&&this[_0xea91ce(0xd0)](_0x3ae020)===_0xea91ce(0x107);},_0x1583f1[_0x2d3e35(0x1a9)][_0x2d3e35(0x17a)]=function(_0x4bd386,_0x39eb82){},_0x1583f1['prototype'][_0x2d3e35(0xc3)]=function(_0x3ee547){var _0x18d945=_0x2d3e35;delete _0x3ee547[_0x18d945(0x112)],delete _0x3ee547[_0x18d945(0x197)],delete _0x3ee547['_hasMapOnItsPath'];},_0x1583f1[_0x2d3e35(0x1a9)][_0x2d3e35(0x10d)]=function(_0x4ae76c,_0x3614a7){};let _0x1cd003=new _0x1583f1(),_0x35f2a4={'props':_0x33b83c['defaultLimits'][_0x2d3e35(0x180)]||0x64,'elements':_0x33b83c[_0x2d3e35(0xdc)][_0x2d3e35(0x10e)]||0x64,'strLength':_0x33b83c[_0x2d3e35(0xdc)][_0x2d3e35(0x110)]||0x400*0x32,'totalStrLength':_0x33b83c['defaultLimits'][_0x2d3e35(0x11c)]||0x400*0x32,'autoExpandLimit':_0x33b83c[_0x2d3e35(0xdc)][_0x2d3e35(0x1c2)]||0x1388,'autoExpandMaxDepth':_0x33b83c['defaultLimits']['autoExpandMaxDepth']||0xa},_0x4f2d54={'props':_0x33b83c['reducedLimits'][_0x2d3e35(0x180)]||0x5,'elements':_0x33b83c[_0x2d3e35(0x163)][_0x2d3e35(0x10e)]||0x5,'strLength':_0x33b83c[_0x2d3e35(0x163)][_0x2d3e35(0x110)]||0x100,'totalStrLength':_0x33b83c[_0x2d3e35(0x163)][_0x2d3e35(0x11c)]||0x100*0x3,'autoExpandLimit':_0x33b83c[_0x2d3e35(0x163)][_0x2d3e35(0x1c2)]||0x1e,'autoExpandMaxDepth':_0x33b83c[_0x2d3e35(0x163)][_0x2d3e35(0xeb)]||0x2};if(_0x1850b6){let _0x1ed37f=_0x1cd003[_0x2d3e35(0x1af)][_0x2d3e35(0x16d)](_0x1cd003);_0x1cd003['serialize']=function(_0x20b9e3,_0xde4523,_0x6dfb6e,_0x45b9d8){return _0x1ed37f(_0x20b9e3,_0x1850b6(_0xde4523),_0x6dfb6e,_0x45b9d8);};}function _0x853c2d(_0x19de88,_0x92ffd3,_0x1d67ec,_0x3c0439,_0x2c965c,_0x5f0da7){var _0x91f555=_0x2d3e35;let _0x28659b,_0x1bec47;try{_0x1bec47=_0x594fe3(),_0x28659b=_0x470e69[_0x92ffd3],!_0x28659b||_0x1bec47-_0x28659b['ts']>_0x13b741[_0x91f555(0xf3)]['resetWhenQuietMs']&&_0x28659b['count']&&_0x28659b[_0x91f555(0x145)]/_0x28659b[_0x91f555(0x1ac)]<_0x13b741[_0x91f555(0xf3)][_0x91f555(0xf5)]?(_0x470e69[_0x92ffd3]=_0x28659b={'count':0x0,'time':0x0,'ts':_0x1bec47},_0x470e69['hits']={}):_0x1bec47-_0x470e69['hits']['ts']>_0x13b741[_0x91f555(0x1c3)][_0x91f555(0x1b1)]&&_0x470e69[_0x91f555(0xd1)]['count']&&_0x470e69[_0x91f555(0xd1)][_0x91f555(0x145)]/_0x470e69['hits']['count']<_0x13b741[_0x91f555(0x1c3)]['resetOnProcessingTimeAverageMs']&&(_0x470e69[_0x91f555(0xd1)]={});let _0x4b3ded=[],_0x506fb2=_0x28659b['reduceLimits']||_0x470e69[_0x91f555(0xd1)][_0x91f555(0x105)]?_0x4f2d54:_0x35f2a4,_0x48c9a7=_0x484e73=>{var _0x3afe8d=_0x91f555;let _0x21d1e7={};return _0x21d1e7[_0x3afe8d(0x180)]=_0x484e73[_0x3afe8d(0x180)],_0x21d1e7[_0x3afe8d(0x10e)]=_0x484e73[_0x3afe8d(0x10e)],_0x21d1e7['strLength']=_0x484e73[_0x3afe8d(0x110)],_0x21d1e7[_0x3afe8d(0x11c)]=_0x484e73[_0x3afe8d(0x11c)],_0x21d1e7[_0x3afe8d(0x1c2)]=_0x484e73['autoExpandLimit'],_0x21d1e7[_0x3afe8d(0xeb)]=_0x484e73[_0x3afe8d(0xeb)],_0x21d1e7[_0x3afe8d(0xd4)]=!0x1,_0x21d1e7['noFunctions']=!_0x4eb197,_0x21d1e7['depth']=0x1,_0x21d1e7['level']=0x0,_0x21d1e7[_0x3afe8d(0x16e)]=_0x3afe8d(0x104),_0x21d1e7[_0x3afe8d(0x186)]='root_exp',_0x21d1e7[_0x3afe8d(0x17e)]=!0x0,_0x21d1e7[_0x3afe8d(0xc2)]=[],_0x21d1e7[_0x3afe8d(0x12a)]=0x0,_0x21d1e7[_0x3afe8d(0x177)]=_0x33b83c['resolveGetters'],_0x21d1e7[_0x3afe8d(0x121)]=0x0,_0x21d1e7[_0x3afe8d(0x125)]={'current':void 0x0,'parent':void 0x0,'index':0x0},_0x21d1e7;};for(var _0x4e686f=0x0;_0x4e686f<_0x2c965c['length'];_0x4e686f++)_0x4b3ded[_0x91f555(0xe6)](_0x1cd003[_0x91f555(0x1af)]({'timeNode':_0x19de88===_0x91f555(0x145)||void 0x0},_0x2c965c[_0x4e686f],_0x48c9a7(_0x506fb2),{}));if(_0x19de88===_0x91f555(0x182)||_0x19de88==='error'){let _0x5e46b1=Error[_0x91f555(0x1b9)];try{Error[_0x91f555(0x1b9)]=0x1/0x0,_0x4b3ded['push'](_0x1cd003['serialize']({'stackNode':!0x0},new Error()[_0x91f555(0x10b)],_0x48c9a7(_0x506fb2),{'strLength':0x1/0x0}));}finally{Error[_0x91f555(0x1b9)]=_0x5e46b1;}}return{'method':'log','version':_0xb46878,'args':[{'ts':_0x1d67ec,'session':_0x3c0439,'args':_0x4b3ded,'id':_0x92ffd3,'context':_0x5f0da7}]};}catch(_0x51d222){return{'method':_0x91f555(0x172),'version':_0xb46878,'args':[{'ts':_0x1d67ec,'session':_0x3c0439,'args':[{'type':_0x91f555(0x14f),'error':_0x51d222&&_0x51d222[_0x91f555(0x1a0)]}],'id':_0x92ffd3,'context':_0x5f0da7}]};}finally{try{if(_0x28659b&&_0x1bec47){let _0x3f4e88=_0x594fe3();_0x28659b[_0x91f555(0x1ac)]++,_0x28659b[_0x91f555(0x145)]+=_0x4120d1(_0x1bec47,_0x3f4e88),_0x28659b['ts']=_0x3f4e88,_0x470e69['hits'][_0x91f555(0x1ac)]++,_0x470e69[_0x91f555(0xd1)][_0x91f555(0x145)]+=_0x4120d1(_0x1bec47,_0x3f4e88),_0x470e69['hits']['ts']=_0x3f4e88,(_0x28659b[_0x91f555(0x1ac)]>_0x13b741[_0x91f555(0xf3)][_0x91f555(0x11f)]||_0x28659b[_0x91f555(0x145)]>_0x13b741[_0x91f555(0xf3)][_0x91f555(0x138)])&&(_0x28659b[_0x91f555(0x105)]=!0x0),(_0x470e69[_0x91f555(0xd1)][_0x91f555(0x1ac)]>_0x13b741['global'][_0x91f555(0x11f)]||_0x470e69['hits'][_0x91f555(0x145)]>_0x13b741[_0x91f555(0x1c3)][_0x91f555(0x138)])&&(_0x470e69['hits'][_0x91f555(0x105)]=!0x0);}}catch{}}}return _0x853c2d;}function G(_0x7dac70){var _0x4fb4c4=_0x34ea16;if(_0x7dac70&&typeof _0x7dac70=='object'&&_0x7dac70[_0x4fb4c4(0x118)])switch(_0x7dac70[_0x4fb4c4(0x118)][_0x4fb4c4(0xc8)]){case _0x4fb4c4(0x14a):return _0x7dac70['hasOwnProperty'](Symbol[_0x4fb4c4(0x19c)])?Promise[_0x4fb4c4(0xfc)]():_0x7dac70;case _0x4fb4c4(0x160):return Promise[_0x4fb4c4(0xfc)]();}return _0x7dac70;}((_0x749b89,_0x2827b8,_0x580b99,_0x418fc9,_0x44c2a9,_0x23ed43,_0x1b9ef6,_0x24f371,_0x5d1b5f,_0x70341a,_0x561d12,_0x5c054b)=>{var _0x25b334=_0x34ea16;if(_0x749b89[_0x25b334(0x1a4)])return _0x749b89[_0x25b334(0x1a4)];let _0x294980={'consoleLog':()=>{},'consoleTrace':()=>{},'consoleTime':()=>{},'consoleTimeEnd':()=>{},'autoLog':()=>{},'autoLogMany':()=>{},'autoTraceMany':()=>{},'coverage':()=>{},'autoTrace':()=>{},'autoTime':()=>{},'autoTimeEnd':()=>{}};if(!X(_0x749b89,_0x24f371,_0x44c2a9))return _0x749b89[_0x25b334(0x1a4)]=_0x294980,_0x749b89[_0x25b334(0x1a4)];let _0x4ac171=b(_0x749b89),_0x44ae90=_0x4ac171['elapsed'],_0x281c3c=_0x4ac171['timeStamp'],_0x156600=_0x4ac171['now'],_0x564ce7={'hits':{},'ts':{}},_0x20d77e=J(_0x749b89,_0x5d1b5f,_0x564ce7,_0x23ed43,_0x5c054b,_0x44c2a9===_0x25b334(0x114)?G:void 0x0),_0x1583f6=(_0x138848,_0x4e327a,_0x410ab7,_0x1afae4,_0xf58f6a,_0x49dcce)=>{var _0x41f612=_0x25b334;let _0x224524=_0x749b89[_0x41f612(0x1a4)];try{return _0x749b89[_0x41f612(0x1a4)]=_0x294980,_0x20d77e(_0x138848,_0x4e327a,_0x410ab7,_0x1afae4,_0xf58f6a,_0x49dcce);}finally{_0x749b89[_0x41f612(0x1a4)]=_0x224524;}},_0x3178b9=_0x202de7=>{_0x564ce7['ts'][_0x202de7]=_0x281c3c();},_0x13cc1b=(_0x1c13ca,_0x42a326)=>{var _0x3ed0b6=_0x25b334;let _0x4c546f=_0x564ce7['ts'][_0x42a326];if(delete _0x564ce7['ts'][_0x42a326],_0x4c546f){let _0x4f11ca=_0x44ae90(_0x4c546f,_0x281c3c());_0xe67207(_0x1583f6(_0x3ed0b6(0x145),_0x1c13ca,_0x156600(),_0x468b04,[_0x4f11ca],_0x42a326));}},_0x53c802=_0x4eb69a=>{var _0x3b77b5=_0x25b334,_0x408cd3;return _0x44c2a9===_0x3b77b5(0x114)&&_0x749b89[_0x3b77b5(0x188)]&&((_0x408cd3=_0x4eb69a==null?void 0x0:_0x4eb69a[_0x3b77b5(0x129)])==null?void 0x0:_0x408cd3['length'])&&(_0x4eb69a['args'][0x0][_0x3b77b5(0x188)]=_0x749b89[_0x3b77b5(0x188)]),_0x4eb69a;};_0x749b89[_0x25b334(0x1a4)]={'consoleLog':(_0x27f701,_0x2ce433)=>{var _0x1a8f8a=_0x25b334;_0x749b89['console']['log'][_0x1a8f8a(0xc8)]!=='disabledLog'&&_0xe67207(_0x1583f6(_0x1a8f8a(0x172),_0x27f701,_0x156600(),_0x468b04,_0x2ce433));},'consoleTrace':(_0x9d0f76,_0x963e49)=>{var _0xbd38e=_0x25b334,_0x291f9e,_0x22bd5e;_0x749b89[_0xbd38e(0xea)][_0xbd38e(0x172)]['name']!==_0xbd38e(0x161)&&((_0x22bd5e=(_0x291f9e=_0x749b89['process'])==null?void 0x0:_0x291f9e['versions'])!=null&&_0x22bd5e['node']&&(_0x749b89[_0xbd38e(0x11a)]=!0x0),_0xe67207(_0x53c802(_0x1583f6('trace',_0x9d0f76,_0x156600(),_0x468b04,_0x963e49))));},'consoleError':(_0x12a0af,_0x39f06f)=>{var _0x3ec0a4=_0x25b334;_0x749b89[_0x3ec0a4(0x11a)]=!0x0,_0xe67207(_0x53c802(_0x1583f6('error',_0x12a0af,_0x156600(),_0x468b04,_0x39f06f)));},'consoleTime':_0x3f0828=>{_0x3178b9(_0x3f0828);},'consoleTimeEnd':(_0x1a4f7e,_0x345701)=>{_0x13cc1b(_0x345701,_0x1a4f7e);},'autoLog':(_0x52f6fa,_0x1cedc2)=>{var _0x418819=_0x25b334;_0xe67207(_0x1583f6(_0x418819(0x172),_0x1cedc2,_0x156600(),_0x468b04,[_0x52f6fa]));},'autoLogMany':(_0x6057b7,_0x1b7e26)=>{var _0x49b88c=_0x25b334;_0xe67207(_0x1583f6(_0x49b88c(0x172),_0x6057b7,_0x156600(),_0x468b04,_0x1b7e26));},'autoTrace':(_0x4b61cf,_0x2766f3)=>{_0xe67207(_0x53c802(_0x1583f6('trace',_0x2766f3,_0x156600(),_0x468b04,[_0x4b61cf])));},'autoTraceMany':(_0x3e5181,_0x16f50e)=>{var _0x286dd8=_0x25b334;_0xe67207(_0x53c802(_0x1583f6(_0x286dd8(0x182),_0x3e5181,_0x156600(),_0x468b04,_0x16f50e)));},'autoTime':(_0x2c4b10,_0x4be44a,_0x2ba611)=>{_0x3178b9(_0x2ba611);},'autoTimeEnd':(_0x5e03ff,_0x5980db,_0x317c23)=>{_0x13cc1b(_0x5980db,_0x317c23);},'coverage':_0x5a8518=>{var _0x3f417c=_0x25b334;_0xe67207({'method':_0x3f417c(0x1c1),'version':_0x23ed43,'args':[{'id':_0x5a8518}]});}};let _0xe67207=H(_0x749b89,_0x2827b8,_0x580b99,_0x418fc9,_0x44c2a9,_0x70341a,_0x561d12),_0x468b04=_0x749b89['_console_ninja_session'];return _0x749b89['_console_ninja'];})(globalThis,_0x34ea16(0xe7),_0x34ea16(0x12c),_0x34ea16(0x133),_0x34ea16(0xe5),'1.0.0',_0x34ea16(0x18e),_0x34ea16(0x1ba),_0x34ea16(0x17f),_0x34ea16(0xd6),'1',{\"resolveGetters\":false,\"defaultLimits\":{\"props\":100,\"elements\":100,\"strLength\":51200,\"totalStrLength\":51200,\"autoExpandLimit\":5000,\"autoExpandMaxDepth\":10},\"reducedLimits\":{\"props\":5,\"elements\":5,\"strLength\":256,\"totalStrLength\":768,\"autoExpandLimit\":30,\"autoExpandMaxDepth\":2},\"reducePolicy\":{\"perLogpoint\":{\"reduceOnCount\":50,\"reduceOnAccumulatedProcessingTimeMs\":100,\"resetWhenQuietMs\":500,\"resetOnProcessingTimeAverageMs\":100},\"global\":{\"reduceOnCount\":1000,\"reduceOnAccumulatedProcessingTimeMs\":300,\"resetWhenQuietMs\":50,\"resetOnProcessingTimeAverageMs\":100}}});");}catch(e){}};/* istanbul ignore next */function oo_oo(i:string,...v:any[]){try{oo_cm().consoleLog(i, v);}catch(e){} return v};oo_oo;/* istanbul ignore next */function oo_tr(i:string,...v:any[]){try{oo_cm().consoleTrace(i, v);}catch(e){} return v};oo_tr;/* istanbul ignore next */function oo_tx(i:string,...v:any[]){try{oo_cm().consoleError(i, v);}catch(e){} return v};oo_tx;/* istanbul ignore next */function oo_ts(v?:string):string{try{oo_cm().consoleTime(v);}catch(e){} return v as string;};oo_ts;/* istanbul ignore next */function oo_te(v:string|undefined, i:string):string{try{oo_cm().consoleTimeEnd(v, i);}catch(e){} return v as string;};oo_te;/*eslint unicorn/no-abusive-eslint-disable:,eslint-comments/disable-enable-pair:,eslint-comments/no-unlimited-disable:,eslint-comments/no-aggregating-enable:,eslint-comments/no-duplicate-disable:,eslint-comments/no-unused-disable:,eslint-comments/no-unused-enable:,*/